/**
 * instruments.js — Web Audio + Web MIDI synthesizer
 *
 * Features:
 *  - Polyphonic ADSR synth via Web Audio API
 *  - Computer keyboard input (Z-M white keys, S D G H J black keys)
 *  - On-screen clickable/touchable keyboard
 *  - Web MIDI API input (auto-connects all available inputs)
 *  - Octave shifting (↑/↓ arrows or on-screen buttons)
 *  - Live ADSR / waveform / volume controls
 *
 * Safari / iOS compatibility notes
 * ──────────────────────────────────
 *  1. Web MIDI API is not supported in Safari. The MIDI section degrades
 *     gracefully: the status banner shows a friendly "not supported" message
 *     and the rest of the synth works normally.
 *  2. iOS Safari requires the AudioContext to be both *created* and *resumed*
 *     synchronously inside a trusted user-gesture handler (tap / click).
 *     `initAudioContext()` is registered on the first `click` and `touchstart`
 *     events so the context is ready before any note fires.  `ensureAudioContext`
 *     is kept as a secondary safety net for code paths that call `noteOn`
 *     directly (e.g. MIDI messages after the context already exists).
 *  3. Safari's AudioContext implementation has several quirks:
 *       a. `linearRampToValueAtTime` misbehaves if there is no prior
 *          automation event at exactly `currentTime`. We always call
 *          `setValueAtTime(currentValue, now)` immediately before any ramp.
 *       b. `cancelScheduledValues` must be followed by a `setValueAtTime`
 *          at the same timestamp or Safari may ignore the cancel.
 *       c. `OscillatorNode.stop(t)` throws if t is in the past. We guard
 *          with `Math.max(t, audioCtx.currentTime + 0.001)`.
 *       d. `webkitAudioContext` is used as a fallback for older Safari.
 *  4. Pointer events on iOS Safari can miss `pointerup`/`pointerleave` for
 *     touch inputs. We attach `touchend`/`touchcancel` listeners as well.
 */ // ─── Note table ───────────────────────────────────────────────────────────────
/**
 * Base frequencies for octave 4 (middle octave).
 * getFrequency() scales these to any octave.
 */ /**
 * drum-machine.js — 16-step drum machine sequencer
 *
 * Features:
 *  - 16 steps for 4 drum voices: kick, snare, hi-hat, tom
 *  - Synthesized drum sounds via Web Audio API (no samples needed)
 *  - Transport: play / stop, BPM control
 *  - Master volume
 *  - Built-in presets (Basic, Funk)
 *  - Clear button
 *  - Visual step playhead
 *  - Look-ahead scheduler for tight timing (same approach as bass-sequencer.js)
 *
 * Safari / iOS compatibility:
 *  - AudioContext created lazily on first play button press
 *  - webkitAudioContext fallback
 *  - exponentialRampToValueAtTime targets clamped to > 0 (Safari throws on 0)
 */ // ─── Constants ────────────────────────────────────────────────────────────────
const $ce32644a0c65456a$var$STEPS = 16;
const $ce32644a0c65456a$var$DRUMS = [
    'kick',
    'snare',
    'hihat',
    'tom'
];
const $ce32644a0c65456a$var$DRUM_LABELS = {
    kick: 'Kick',
    snare: 'Snare',
    hihat: 'Hi-Hat',
    tom: 'Tom'
};
/**
 * Look-ahead scheduler constants — mirrors bass-sequencer.js for consistency.
 */ const $ce32644a0c65456a$var$LOOKAHEAD_MS = 100.0;
const $ce32644a0c65456a$var$SCHEDULE_INTERVAL_MS = 50.0;
// ─── State ────────────────────────────────────────────────────────────────────
/**
 * Web Audio context — injected by instruments.js via setSharedAudioContext()
 * so all instruments share one context and can be recorded together.
 * Falls back to a locally-created context if called standalone.
 */ let $ce32644a0c65456a$var$audioCtx = null;
/**
 * Per-module gain node that sits between drum voices and the shared drumGain.
 * Drum voices → drumGain → sharedMasterGain → destination (+ recordingDest)
 */ let $ce32644a0c65456a$var$drumGain = null;
/** Whether the sequencer is currently running. */ let $ce32644a0c65456a$var$isPlaying = false;
/** Index of the next step to be scheduled (0-based). */ let $ce32644a0c65456a$var$currentStep = 0;
/** setTimeout ID for the look-ahead scheduler. */ let $ce32644a0c65456a$var$schedulerTimer = null;
/** Absolute AudioContext time for the next step. */ let $ce32644a0c65456a$var$nextStepTime = 0;
/** Sequencer parameters — kept in sync with UI controls. */ const $ce32644a0c65456a$var$params = {
    bpm: 120,
    volume: 0.6,
    kickPitch: 150,
    kickDecay: 0.45,
    snareDecay: 0.22,
    hihatDecay: 0.08,
    tomDecay: 0.18
};
/**
 * Pattern: 16 steps × 4 drums.
 * pattern[stepIndex][drumName] = boolean
 */ let $ce32644a0c65456a$var$pattern = $ce32644a0c65456a$var$buildEmptyPattern();
function $ce32644a0c65456a$var$buildEmptyPattern() {
    return Array.from({
        length: $ce32644a0c65456a$var$STEPS
    }, ()=>({
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        }));
}
// ─── Presets ──────────────────────────────────────────────────────────────────
const $ce32644a0c65456a$var$PRESETS = {
    basic: [
        {
            kick: true,
            snare: false,
            hihat: true,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: true,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: false,
            snare: true,
            hihat: true,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: true,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: true,
            snare: false,
            hihat: true,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: true,
            tom: false
        },
        {
            kick: true,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: false,
            snare: true,
            hihat: true,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: true,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        }
    ],
    funk: [
        {
            kick: true,
            snare: false,
            hihat: true,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: true,
            tom: true
        },
        {
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: false,
            snare: true,
            hihat: true,
            tom: false
        },
        {
            kick: true,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: true,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: true,
            snare: false,
            hihat: true,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: true,
            tom: true
        },
        {
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: false,
            snare: true,
            hihat: true,
            tom: false
        },
        {
            kick: true,
            snare: false,
            hihat: false,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: true,
            tom: false
        },
        {
            kick: false,
            snare: false,
            hihat: false,
            tom: false
        }
    ]
};
function $ce32644a0c65456a$export$5dc7305e084869ea(ctx, sharedMasterGain) {
    $ce32644a0c65456a$var$audioCtx = ctx;
    $ce32644a0c65456a$var$drumGain = ctx.createGain();
    $ce32644a0c65456a$var$drumGain.gain.value = $ce32644a0c65456a$var$params.volume;
    $ce32644a0c65456a$var$drumGain.connect(sharedMasterGain);
}
function $ce32644a0c65456a$var$ensureAudioContext() {
    // If the shared context was injected, we're already good
    if ($ce32644a0c65456a$var$audioCtx && $ce32644a0c65456a$var$drumGain) {
        if ($ce32644a0c65456a$var$audioCtx.state === 'suspended') $ce32644a0c65456a$var$audioCtx.resume();
        return;
    }
    // Fallback: create a standalone context (e.g. if used without instruments.js)
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    $ce32644a0c65456a$var$audioCtx = new AC();
    $ce32644a0c65456a$var$drumGain = $ce32644a0c65456a$var$audioCtx.createGain();
    $ce32644a0c65456a$var$drumGain.gain.value = $ce32644a0c65456a$var$params.volume;
    $ce32644a0c65456a$var$drumGain.connect($ce32644a0c65456a$var$audioCtx.destination);
}
// ─── Drum synthesis ───────────────────────────────────────────────────────────
/**
 * Synthesise a kick drum: sine oscillator with a fast frequency sweep
 * from kickPitch down to near-silence, shaped by an exponential gain envelope.
 */ function $ce32644a0c65456a$var$playKick(time) {
    if (!$ce32644a0c65456a$var$audioCtx || !$ce32644a0c65456a$var$drumGain) return;
    const osc = $ce32644a0c65456a$var$audioCtx.createOscillator();
    const gain = $ce32644a0c65456a$var$audioCtx.createGain();
    osc.connect(gain);
    gain.connect($ce32644a0c65456a$var$drumGain);
    osc.type = 'sine';
    osc.frequency.setValueAtTime($ce32644a0c65456a$var$params.kickPitch, time);
    osc.frequency.exponentialRampToValueAtTime(0.001, time + $ce32644a0c65456a$var$params.kickDecay);
    gain.gain.setValueAtTime($ce32644a0c65456a$var$params.volume * 0.9, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + $ce32644a0c65456a$var$params.kickDecay);
    osc.start(time);
    osc.stop(time + $ce32644a0c65456a$var$params.kickDecay + 0.01);
}
/**
 * Synthesise a snare drum: filtered white noise burst.
 * High-pass filter at 3 kHz gives the crisp "snap" of a snare.
 */ function $ce32644a0c65456a$var$playSnare(time) {
    if (!$ce32644a0c65456a$var$audioCtx || !$ce32644a0c65456a$var$drumGain) return;
    const bufferLen = Math.ceil($ce32644a0c65456a$var$audioCtx.sampleRate * $ce32644a0c65456a$var$params.snareDecay);
    const noiseBuffer = $ce32644a0c65456a$var$audioCtx.createBuffer(1, bufferLen, $ce32644a0c65456a$var$audioCtx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for(let i = 0; i < bufferLen; i++)data[i] = Math.random() * 2 - 1;
    const noise = $ce32644a0c65456a$var$audioCtx.createBufferSource();
    noise.buffer = noiseBuffer;
    const filter = $ce32644a0c65456a$var$audioCtx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 3000;
    filter.Q.value = 0.5;
    const gain = $ce32644a0c65456a$var$audioCtx.createGain();
    gain.gain.setValueAtTime($ce32644a0c65456a$var$params.volume * 0.75, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + $ce32644a0c65456a$var$params.snareDecay);
    noise.connect(filter);
    filter.connect(gain);
    gain.connect($ce32644a0c65456a$var$drumGain);
    noise.start(time);
    noise.stop(time + $ce32644a0c65456a$var$params.snareDecay + 0.01);
}
/**
 * Synthesise a closed hi-hat: short burst of very high-pass filtered noise.
 */ function $ce32644a0c65456a$var$playHihat(time) {
    if (!$ce32644a0c65456a$var$audioCtx || !$ce32644a0c65456a$var$drumGain) return;
    const bufferLen = Math.ceil($ce32644a0c65456a$var$audioCtx.sampleRate * $ce32644a0c65456a$var$params.hihatDecay);
    const noiseBuffer = $ce32644a0c65456a$var$audioCtx.createBuffer(1, bufferLen, $ce32644a0c65456a$var$audioCtx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for(let i = 0; i < bufferLen; i++)data[i] = Math.random() * 2 - 1;
    const noise = $ce32644a0c65456a$var$audioCtx.createBufferSource();
    noise.buffer = noiseBuffer;
    const filter = $ce32644a0c65456a$var$audioCtx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 10000;
    filter.Q.value = 0.8;
    const gain = $ce32644a0c65456a$var$audioCtx.createGain();
    gain.gain.setValueAtTime($ce32644a0c65456a$var$params.volume * 0.5, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + $ce32644a0c65456a$var$params.hihatDecay);
    noise.connect(filter);
    filter.connect(gain);
    gain.connect($ce32644a0c65456a$var$drumGain);
    noise.start(time);
    noise.stop(time + $ce32644a0c65456a$var$params.hihatDecay + 0.01);
}
/**
 * Synthesise a floor tom: sine oscillator with a moderate pitch sweep,
 * lower in pitch and longer in decay than the kick.
 */ function $ce32644a0c65456a$var$playTom(time) {
    if (!$ce32644a0c65456a$var$audioCtx || !$ce32644a0c65456a$var$drumGain) return;
    const osc = $ce32644a0c65456a$var$audioCtx.createOscillator();
    const gain = $ce32644a0c65456a$var$audioCtx.createGain();
    osc.connect(gain);
    gain.connect($ce32644a0c65456a$var$drumGain);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(220, time);
    osc.frequency.exponentialRampToValueAtTime(80, time + $ce32644a0c65456a$var$params.tomDecay);
    gain.gain.setValueAtTime($ce32644a0c65456a$var$params.volume * 0.7, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + $ce32644a0c65456a$var$params.tomDecay);
    osc.start(time);
    osc.stop(time + $ce32644a0c65456a$var$params.tomDecay + 0.01);
}
const $ce32644a0c65456a$var$PLAY_FNS = {
    kick: $ce32644a0c65456a$var$playKick,
    snare: $ce32644a0c65456a$var$playSnare,
    hihat: $ce32644a0c65456a$var$playHihat,
    tom: $ce32644a0c65456a$var$playTom
};
// ─── Scheduler ────────────────────────────────────────────────────────────────
/** Duration of one 16th-note step in seconds at the current BPM. */ function $ce32644a0c65456a$var$stepDuration() {
    return 60 / $ce32644a0c65456a$var$params.bpm / 4;
}
/** Schedule audio + visual for a single step. */ function $ce32644a0c65456a$var$scheduleStep(stepIdx, time) {
    const step = $ce32644a0c65456a$var$pattern[stepIdx];
    $ce32644a0c65456a$var$DRUMS.forEach((drum)=>{
        if (step[drum]) $ce32644a0c65456a$var$PLAY_FNS[drum](time);
    });
    // Visual playhead — delayed to match audio timing
    const delayMs = Math.max(0, (time - $ce32644a0c65456a$var$audioCtx.currentTime) * 1000);
    setTimeout(()=>$ce32644a0c65456a$var$updatePlayhead(stepIdx), delayMs);
}
/** Look-ahead scheduler — fires every SCHEDULE_INTERVAL_MS. */ function $ce32644a0c65456a$var$scheduler() {
    if (!$ce32644a0c65456a$var$audioCtx) return;
    const lookAheadSec = $ce32644a0c65456a$var$LOOKAHEAD_MS / 1000;
    while($ce32644a0c65456a$var$nextStepTime < $ce32644a0c65456a$var$audioCtx.currentTime + lookAheadSec){
        $ce32644a0c65456a$var$scheduleStep($ce32644a0c65456a$var$currentStep, $ce32644a0c65456a$var$nextStepTime);
        $ce32644a0c65456a$var$nextStepTime += $ce32644a0c65456a$var$stepDuration();
        $ce32644a0c65456a$var$currentStep = ($ce32644a0c65456a$var$currentStep + 1) % $ce32644a0c65456a$var$STEPS;
    }
    $ce32644a0c65456a$var$schedulerTimer = setTimeout($ce32644a0c65456a$var$scheduler, $ce32644a0c65456a$var$SCHEDULE_INTERVAL_MS);
}
// ─── Transport ────────────────────────────────────────────────────────────────
function $ce32644a0c65456a$var$startSequencer() {
    $ce32644a0c65456a$var$ensureAudioContext();
    if (!$ce32644a0c65456a$var$audioCtx) return;
    if ($ce32644a0c65456a$var$audioCtx.state === 'suspended') $ce32644a0c65456a$var$audioCtx.resume();
    $ce32644a0c65456a$var$isPlaying = true;
    $ce32644a0c65456a$var$currentStep = 0;
    $ce32644a0c65456a$var$nextStepTime = $ce32644a0c65456a$var$audioCtx.currentTime + 0.05;
    $ce32644a0c65456a$var$scheduler();
    $ce32644a0c65456a$var$updatePlayBtn();
}
function $ce32644a0c65456a$var$stopSequencer() {
    $ce32644a0c65456a$var$isPlaying = false;
    if ($ce32644a0c65456a$var$schedulerTimer !== null) {
        clearTimeout($ce32644a0c65456a$var$schedulerTimer);
        $ce32644a0c65456a$var$schedulerTimer = null;
    }
    $ce32644a0c65456a$var$clearPlayhead();
    $ce32644a0c65456a$var$updatePlayBtn();
}
function $ce32644a0c65456a$var$togglePlay() {
    if ($ce32644a0c65456a$var$isPlaying) $ce32644a0c65456a$var$stopSequencer();
    else $ce32644a0c65456a$var$startSequencer();
}
// ─── Pattern helpers ──────────────────────────────────────────────────────────
function $ce32644a0c65456a$var$toggleStep(stepIdx, drum) {
    $ce32644a0c65456a$var$pattern[stepIdx][drum] = !$ce32644a0c65456a$var$pattern[stepIdx][drum];
    const btn = document.querySelector(`#drum-grid .drum-step[data-step="${stepIdx}"][data-drum="${drum}"]`);
    if (!btn) return;
    btn.classList.toggle('drum-step--active', $ce32644a0c65456a$var$pattern[stepIdx][drum]);
    btn.setAttribute('aria-pressed', String($ce32644a0c65456a$var$pattern[stepIdx][drum]));
}
function $ce32644a0c65456a$var$clearPattern() {
    $ce32644a0c65456a$var$pattern = $ce32644a0c65456a$var$buildEmptyPattern();
    document.querySelectorAll('#drum-grid .drum-step').forEach((btn)=>{
        btn.classList.remove('drum-step--active');
        btn.setAttribute('aria-pressed', 'false');
    });
}
function $ce32644a0c65456a$var$applyPreset(name) {
    const preset = $ce32644a0c65456a$var$PRESETS[name];
    if (!preset) return;
    $ce32644a0c65456a$var$pattern = preset.map((step)=>({
            ...step
        }));
    $ce32644a0c65456a$var$DRUMS.forEach((drum)=>{
        for(let i = 0; i < $ce32644a0c65456a$var$STEPS; i++){
            const btn = document.querySelector(`#drum-grid .drum-step[data-step="${i}"][data-drum="${drum}"]`);
            if (!btn) continue;
            btn.classList.toggle('drum-step--active', $ce32644a0c65456a$var$pattern[i][drum]);
            btn.setAttribute('aria-pressed', String($ce32644a0c65456a$var$pattern[i][drum]));
        }
    });
}
// ─── Playhead ─────────────────────────────────────────────────────────────────
let $ce32644a0c65456a$var$lastPlayheadStep = -1;
function $ce32644a0c65456a$var$updatePlayhead(stepIdx) {
    if (!$ce32644a0c65456a$var$isPlaying) return;
    if ($ce32644a0c65456a$var$lastPlayheadStep >= 0) document.querySelectorAll(`#drum-grid .drum-step[data-step="${$ce32644a0c65456a$var$lastPlayheadStep}"]`).forEach((btn)=>btn.classList.remove('drum-step--playing'));
    document.querySelectorAll(`#drum-grid .drum-step[data-step="${stepIdx}"]`).forEach((btn)=>btn.classList.add('drum-step--playing'));
    $ce32644a0c65456a$var$lastPlayheadStep = stepIdx;
}
function $ce32644a0c65456a$var$clearPlayhead() {
    if ($ce32644a0c65456a$var$lastPlayheadStep >= 0) {
        document.querySelectorAll(`#drum-grid .drum-step[data-step="${$ce32644a0c65456a$var$lastPlayheadStep}"]`).forEach((btn)=>btn.classList.remove('drum-step--playing'));
        $ce32644a0c65456a$var$lastPlayheadStep = -1;
    }
}
// ─── Play button ──────────────────────────────────────────────────────────────
function $ce32644a0c65456a$var$updatePlayBtn() {
    const btn = document.getElementById('drum-play-btn');
    if (!btn) return;
    btn.setAttribute('aria-pressed', String($ce32644a0c65456a$var$isPlaying));
    btn.textContent = $ce32644a0c65456a$var$isPlaying ? "\u25A0 Stop" : "\u25B6 Play";
}
// ─── Grid builder ─────────────────────────────────────────────────────────────
function $ce32644a0c65456a$var$buildGrid() {
    const grid = document.getElementById('drum-grid');
    if (!grid) return;
    grid.innerHTML = '';
    $ce32644a0c65456a$var$DRUMS.forEach((drum)=>{
        const row = document.createElement('div');
        row.className = 'drum-row';
        // Row label
        const label = document.createElement('span');
        label.className = 'drum-label';
        label.textContent = $ce32644a0c65456a$var$DRUM_LABELS[drum];
        row.appendChild(label);
        // Step buttons
        for(let i = 0; i < $ce32644a0c65456a$var$STEPS; i++){
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'drum-step';
            btn.dataset.step = String(i);
            btn.dataset.drum = drum;
            btn.setAttribute('aria-label', `${$ce32644a0c65456a$var$DRUM_LABELS[drum]} step ${i + 1}`);
            btn.setAttribute('aria-pressed', 'false');
            // Beat number hint inside first step of each beat
            if (i % 4 === 0) {
                const mark = document.createElement('span');
                mark.className = 'drum-beat-mark';
                mark.textContent = String(i / 4 + 1);
                mark.setAttribute('aria-hidden', 'true');
                btn.appendChild(mark);
            }
            btn.addEventListener('click', ()=>$ce32644a0c65456a$var$toggleStep(i, drum));
            row.appendChild(btn);
        }
        grid.appendChild(row);
    });
}
// ─── Controls ─────────────────────────────────────────────────────────────────
function $ce32644a0c65456a$var$initControls() {
    // Play / stop
    document.getElementById('drum-play-btn')?.addEventListener('click', $ce32644a0c65456a$var$togglePlay);
    // Clear
    document.getElementById('drum-clear-btn')?.addEventListener('click', $ce32644a0c65456a$var$clearPattern);
    // Presets
    document.querySelectorAll('[data-drum-preset]').forEach((btn)=>{
        btn.addEventListener('click', ()=>$ce32644a0c65456a$var$applyPreset(btn.dataset.drumPreset));
    });
    // Volume
    const volInput = document.getElementById('drum-volume');
    const volValue = document.getElementById('drum-volume-value');
    volInput?.addEventListener('input', ()=>{
        $ce32644a0c65456a$var$params.volume = parseFloat(volInput.value);
        if (volValue) volValue.textContent = `${Math.round($ce32644a0c65456a$var$params.volume * 100)}%`;
        if ($ce32644a0c65456a$var$drumGain && $ce32644a0c65456a$var$audioCtx) $ce32644a0c65456a$var$drumGain.gain.setTargetAtTime($ce32644a0c65456a$var$params.volume, $ce32644a0c65456a$var$audioCtx.currentTime, 0.01);
    });
    if (volValue && volInput) volValue.textContent = `${Math.round(parseFloat(volInput.value) * 100)}%`;
}
function $ce32644a0c65456a$export$f8f2055a7d4756f6(bpm) {
    $ce32644a0c65456a$var$params.bpm = bpm;
}
function $ce32644a0c65456a$export$ec09ef61b8a66e8f() {
    if ($ce32644a0c65456a$var$isPlaying) $ce32644a0c65456a$var$stopSequencer();
}
function $ce32644a0c65456a$export$bd10d0af299f079e() {
    return $ce32644a0c65456a$var$drumGain;
}
function $ce32644a0c65456a$export$95dc3c842d06fdc6() {
    $ce32644a0c65456a$var$buildGrid();
    $ce32644a0c65456a$var$initControls();
}


/**
 * bass-sequencer.js — 16-step bass line sequencer
 *
 * Features:
 *  - 16 steps, each with an independent note selector
 *  - Transport: play / stop, BPM, note length
 *  - Oscillator waveform selector
 *  - Master volume
 *  - ADSR envelope (collapsible)
 *  - Built-in pattern presets (Classic, Walking, Funk)
 *  - Clear button
 *  - Visual step playhead
 *  - Responsive: collapses to 8 columns on narrow screens
 */ // ─── Constants ────────────────────────────────────────────────────────────────
const $c424a9d7bd65f9e1$var$STEPS = 16;
/** Bass-range notes available in the per-step selector. */ const $c424a9d7bd65f9e1$var$NOTES = [
    'E1',
    'F1',
    'F#1',
    'G1',
    'G#1',
    'A1',
    'A#1',
    'B1',
    'C2',
    'C#2',
    'D2',
    'D#2',
    'E2',
    'F2',
    'F#2',
    'G2',
    'G#2',
    'A2',
    'A#2',
    'B2',
    'C3',
    'C#3',
    'D3',
    'D#3',
    'E3',
    'F3',
    'F#3',
    'G3'
];
/** Frequencies for every note in NOTES. */ const $c424a9d7bd65f9e1$var$NOTE_FREQS = (()=>{
    // Equal temperament: A4 = 440 Hz
    const A4_MIDI = 69;
    const A4_FREQ = 440;
    const noteNames = [
        'C',
        'C#',
        'D',
        'D#',
        'E',
        'F',
        'F#',
        'G',
        'G#',
        'A',
        'A#',
        'B'
    ];
    function midiFreq(midi) {
        return A4_FREQ * Math.pow(2, (midi - A4_MIDI) / 12);
    }
    function noteToMidi(name) {
        // e.g. "F#2" → note index + octave
        const match = name.match(/^([A-G]#?)(\d)$/);
        if (!match) return 60;
        const noteIdx = noteNames.indexOf(match[1]);
        const octave = parseInt(match[2], 10);
        return (octave + 1) * 12 + noteIdx;
    }
    const map = {};
    for (const n of $c424a9d7bd65f9e1$var$NOTES)map[n] = midiFreq(noteToMidi(n));
    return map;
})();
/** Default note for each new step. */ const $c424a9d7bd65f9e1$var$DEFAULT_NOTE = 'E2';
/** Preset patterns: arrays of { active, note } for each of the 16 steps. */ const $c424a9d7bd65f9e1$var$PRESETS = {
    classic: [
        {
            active: true,
            note: 'E2'
        },
        {
            active: false,
            note: 'E2'
        },
        {
            active: false,
            note: 'E2'
        },
        {
            active: true,
            note: 'E2'
        },
        {
            active: false,
            note: 'E2'
        },
        {
            active: false,
            note: 'E2'
        },
        {
            active: true,
            note: 'G2'
        },
        {
            active: false,
            note: 'G2'
        },
        {
            active: true,
            note: 'A2'
        },
        {
            active: false,
            note: 'A2'
        },
        {
            active: false,
            note: 'A2'
        },
        {
            active: true,
            note: 'A2'
        },
        {
            active: false,
            note: 'A2'
        },
        {
            active: true,
            note: 'G2'
        },
        {
            active: false,
            note: 'G2'
        },
        {
            active: false,
            note: 'E2'
        }
    ],
    walking: [
        {
            active: true,
            note: 'E2'
        },
        {
            active: true,
            note: 'F#2'
        },
        {
            active: true,
            note: 'G2'
        },
        {
            active: true,
            note: 'A2'
        },
        {
            active: true,
            note: 'B2'
        },
        {
            active: true,
            note: 'A2'
        },
        {
            active: true,
            note: 'G2'
        },
        {
            active: true,
            note: 'F#2'
        },
        {
            active: true,
            note: 'E2'
        },
        {
            active: true,
            note: 'D2'
        },
        {
            active: true,
            note: 'C2'
        },
        {
            active: true,
            note: 'B1'
        },
        {
            active: true,
            note: 'A1'
        },
        {
            active: true,
            note: 'B1'
        },
        {
            active: true,
            note: 'C2'
        },
        {
            active: true,
            note: 'D2'
        }
    ],
    funk: [
        {
            active: true,
            note: 'E2'
        },
        {
            active: false,
            note: 'E2'
        },
        {
            active: true,
            note: 'E2'
        },
        {
            active: false,
            note: 'G2'
        },
        {
            active: true,
            note: 'A2'
        },
        {
            active: false,
            note: 'A2'
        },
        {
            active: false,
            note: 'A2'
        },
        {
            active: true,
            note: 'G2'
        },
        {
            active: true,
            note: 'E2'
        },
        {
            active: false,
            note: 'E2'
        },
        {
            active: true,
            note: 'E2'
        },
        {
            active: true,
            note: 'D2'
        },
        {
            active: false,
            note: 'D2'
        },
        {
            active: true,
            note: 'E2'
        },
        {
            active: false,
            note: 'E2'
        },
        {
            active: false,
            note: 'G2'
        }
    ]
};
// ─── State ────────────────────────────────────────────────────────────────────
/**
 * Web Audio context — injected by instruments.js via setSharedAudioContext()
 * so all instruments share one context and can be recorded together.
 * Falls back to a locally-created context if called standalone.
 */ let $c424a9d7bd65f9e1$var$audioCtx = null;
/**
 * Per-module gain node that sits between bass oscillators and the shared masterGain.
 * Bass voices → bassGain → sharedMasterGain → destination (+ recordingDest)
 */ let $c424a9d7bd65f9e1$var$bassGain = null;
/** Whether the sequencer is currently running. */ let $c424a9d7bd65f9e1$var$isPlaying = false;
/** Index of the step currently being scheduled (0-based). */ let $c424a9d7bd65f9e1$var$currentStep = 0;
/** Web Audio scheduler — the ID returned by setTimeout. */ let $c424a9d7bd65f9e1$var$schedulerTimer = null;
/**
 * The "look-ahead" scheduler uses two values:
 *  - LOOKAHEAD_MS: how far ahead (ms) to schedule notes
 *  - SCHEDULE_INTERVAL_MS: how often the scheduler function runs
 *
 * This keeps audio tight even when the JS thread is busy.
 */ const $c424a9d7bd65f9e1$var$LOOKAHEAD_MS = 100.0;
const $c424a9d7bd65f9e1$var$SCHEDULE_INTERVAL_MS = 50.0;
/** Absolute AudioContext time for the next step. */ let $c424a9d7bd65f9e1$var$nextStepTime = 0;
/** Sequencer parameters (kept in sync with UI). */ const $c424a9d7bd65f9e1$var$params = {
    bpm: 120,
    noteLen: 0.5,
    waveform: 'sawtooth',
    volume: 0.5,
    attack: 0.005,
    decay: 0.08,
    sustain: 0.5,
    release: 0.15
};
/**
 * Per-step data: active flag + note name.
 * Populated by buildGrid().
 */ const $c424a9d7bd65f9e1$var$steps = Array.from({
    length: $c424a9d7bd65f9e1$var$STEPS
}, ()=>({
        active: false,
        note: $c424a9d7bd65f9e1$var$DEFAULT_NOTE
    }));
function $c424a9d7bd65f9e1$export$5dc7305e084869ea(ctx, sharedMasterGain) {
    $c424a9d7bd65f9e1$var$audioCtx = ctx;
    $c424a9d7bd65f9e1$var$bassGain = ctx.createGain();
    $c424a9d7bd65f9e1$var$bassGain.gain.value = $c424a9d7bd65f9e1$var$params.volume;
    $c424a9d7bd65f9e1$var$bassGain.connect(sharedMasterGain);
}
function $c424a9d7bd65f9e1$var$ensureAudioContext() {
    // If the shared context was injected, we're already good
    if ($c424a9d7bd65f9e1$var$audioCtx && $c424a9d7bd65f9e1$var$bassGain) return;
    // Fallback: create a standalone context (e.g. if used without instruments.js)
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    $c424a9d7bd65f9e1$var$audioCtx = new AC();
    $c424a9d7bd65f9e1$var$bassGain = $c424a9d7bd65f9e1$var$audioCtx.createGain();
    $c424a9d7bd65f9e1$var$bassGain.gain.value = $c424a9d7bd65f9e1$var$params.volume;
    $c424a9d7bd65f9e1$var$bassGain.connect($c424a9d7bd65f9e1$var$audioCtx.destination);
}
/** Schedule a single bass note at a precise AudioContext time. */ function $c424a9d7bd65f9e1$var$scheduleNote(freq, startTime, stepDur) {
    if (!$c424a9d7bd65f9e1$var$audioCtx || !$c424a9d7bd65f9e1$var$bassGain) return;
    const noteDuration = stepDur * $c424a9d7bd65f9e1$var$params.noteLen;
    const osc = $c424a9d7bd65f9e1$var$audioCtx.createOscillator();
    osc.type = $c424a9d7bd65f9e1$var$params.waveform;
    osc.frequency.value = freq;
    const envGain = $c424a9d7bd65f9e1$var$audioCtx.createGain();
    envGain.gain.setValueAtTime(0, startTime);
    // Attack
    const attackEnd = startTime + Math.max($c424a9d7bd65f9e1$var$params.attack, 0.001);
    envGain.gain.linearRampToValueAtTime(1.0, attackEnd);
    // Decay → Sustain
    const decayEnd = attackEnd + Math.max($c424a9d7bd65f9e1$var$params.decay, 0.001);
    envGain.gain.linearRampToValueAtTime($c424a9d7bd65f9e1$var$params.sustain, decayEnd);
    // Release
    const noteEnd = startTime + noteDuration;
    const releaseEnd = noteEnd + Math.max($c424a9d7bd65f9e1$var$params.release, 0.001);
    envGain.gain.setValueAtTime($c424a9d7bd65f9e1$var$params.sustain, noteEnd);
    envGain.gain.linearRampToValueAtTime(0, releaseEnd);
    osc.connect(envGain);
    envGain.connect($c424a9d7bd65f9e1$var$bassGain);
    osc.start(startTime);
    osc.stop(releaseEnd + 0.01);
}
// ─── Scheduler ────────────────────────────────────────────────────────────────
/** Duration of one 16th-note step in seconds at the current BPM. */ function $c424a9d7bd65f9e1$var$stepDuration() {
    // 1 beat = 60 / BPM seconds; 1 step = 1 beat / 4 (16th notes)
    return 60 / $c424a9d7bd65f9e1$var$params.bpm / 4;
}
/**
 * Look-ahead scheduler — runs every SCHEDULE_INTERVAL_MS ms.
 * Schedules all steps whose start time falls within the look-ahead window.
 */ function $c424a9d7bd65f9e1$var$scheduler() {
    if (!$c424a9d7bd65f9e1$var$audioCtx) return;
    const lookAheadSec = $c424a9d7bd65f9e1$var$LOOKAHEAD_MS / 1000;
    while($c424a9d7bd65f9e1$var$nextStepTime < $c424a9d7bd65f9e1$var$audioCtx.currentTime + lookAheadSec){
        $c424a9d7bd65f9e1$var$scheduleStep($c424a9d7bd65f9e1$var$currentStep, $c424a9d7bd65f9e1$var$nextStepTime);
        // Advance
        $c424a9d7bd65f9e1$var$nextStepTime += $c424a9d7bd65f9e1$var$stepDuration();
        $c424a9d7bd65f9e1$var$currentStep = ($c424a9d7bd65f9e1$var$currentStep + 1) % $c424a9d7bd65f9e1$var$STEPS;
    }
    $c424a9d7bd65f9e1$var$schedulerTimer = setTimeout($c424a9d7bd65f9e1$var$scheduler, $c424a9d7bd65f9e1$var$SCHEDULE_INTERVAL_MS);
}
/** Schedule (or skip) a single step. Also updates the visual playhead. */ function $c424a9d7bd65f9e1$var$scheduleStep(stepIdx, time) {
    const step = $c424a9d7bd65f9e1$var$steps[stepIdx];
    if (step.active) {
        const freq = $c424a9d7bd65f9e1$var$NOTE_FREQS[step.note];
        if (freq) $c424a9d7bd65f9e1$var$scheduleNote(freq, time, $c424a9d7bd65f9e1$var$stepDuration());
    }
    // Update visual playhead at the correct time using AudioContext timing
    const delay = Math.max(0, (time - $c424a9d7bd65f9e1$var$audioCtx.currentTime) * 1000);
    setTimeout(()=>$c424a9d7bd65f9e1$var$updatePlayhead(stepIdx), delay);
}
// ─── Transport ────────────────────────────────────────────────────────────────
function $c424a9d7bd65f9e1$var$startSequencer() {
    $c424a9d7bd65f9e1$var$ensureAudioContext();
    if (!$c424a9d7bd65f9e1$var$audioCtx) return;
    if ($c424a9d7bd65f9e1$var$audioCtx.state === 'suspended') $c424a9d7bd65f9e1$var$audioCtx.resume();
    $c424a9d7bd65f9e1$var$isPlaying = true;
    $c424a9d7bd65f9e1$var$currentStep = 0;
    $c424a9d7bd65f9e1$var$nextStepTime = $c424a9d7bd65f9e1$var$audioCtx.currentTime + 0.05; // small initial offset
    $c424a9d7bd65f9e1$var$scheduler();
    $c424a9d7bd65f9e1$var$updatePlayBtn();
}
function $c424a9d7bd65f9e1$var$stopSequencer() {
    $c424a9d7bd65f9e1$var$isPlaying = false;
    if ($c424a9d7bd65f9e1$var$schedulerTimer !== null) {
        clearTimeout($c424a9d7bd65f9e1$var$schedulerTimer);
        $c424a9d7bd65f9e1$var$schedulerTimer = null;
    }
    $c424a9d7bd65f9e1$var$clearPlayhead();
    $c424a9d7bd65f9e1$var$updatePlayBtn();
}
function $c424a9d7bd65f9e1$var$togglePlay() {
    if ($c424a9d7bd65f9e1$var$isPlaying) $c424a9d7bd65f9e1$var$stopSequencer();
    else $c424a9d7bd65f9e1$var$startSequencer();
}
// ─── Grid ─────────────────────────────────────────────────────────────────────
function $c424a9d7bd65f9e1$var$buildGrid() {
    const grid = document.getElementById('seq-grid');
    if (!grid) return;
    grid.innerHTML = '';
    for(let i = 0; i < $c424a9d7bd65f9e1$var$STEPS; i++){
        const col = document.createElement('div');
        col.className = 'seq-step-col';
        // Beat marker (1-indexed, shown at steps 0, 4, 8, 12)
        const beatMark = document.createElement('span');
        beatMark.className = i % 4 === 0 ? 'seq-beat-mark' : 'seq-beat-mark seq-beat-mark--spacer';
        beatMark.textContent = i % 4 === 0 ? String(i / 4 + 1) : ' ';
        col.appendChild(beatMark);
        // Step toggle button
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'seq-step';
        btn.dataset.step = String(i);
        btn.setAttribute('aria-label', `Step ${i + 1}`);
        btn.setAttribute('aria-pressed', 'false');
        const num = document.createElement('span');
        num.className = 'seq-step-num';
        num.textContent = String(i + 1);
        btn.appendChild(num);
        btn.addEventListener('click', ()=>$c424a9d7bd65f9e1$var$toggleStep(i));
        col.appendChild(btn);
        // Note selector
        const sel = document.createElement('select');
        sel.className = 'seq-note-select';
        sel.dataset.step = String(i);
        sel.setAttribute('aria-label', `Note for step ${i + 1}`);
        for (const note of $c424a9d7bd65f9e1$var$NOTES){
            const opt = document.createElement('option');
            opt.value = note;
            opt.textContent = note;
            if (note === $c424a9d7bd65f9e1$var$DEFAULT_NOTE) opt.selected = true;
            sel.appendChild(opt);
        }
        sel.addEventListener('change', ()=>{
            $c424a9d7bd65f9e1$var$steps[i].note = sel.value;
        });
        col.appendChild(sel);
        grid.appendChild(col);
    }
}
function $c424a9d7bd65f9e1$var$toggleStep(idx) {
    $c424a9d7bd65f9e1$var$steps[idx].active = !$c424a9d7bd65f9e1$var$steps[idx].active;
    const btn = document.querySelector(`.seq-step[data-step="${idx}"]`);
    if (!btn) return;
    btn.classList.toggle('seq-step--active', $c424a9d7bd65f9e1$var$steps[idx].active);
    btn.setAttribute('aria-pressed', String($c424a9d7bd65f9e1$var$steps[idx].active));
}
function $c424a9d7bd65f9e1$var$applyPreset(name) {
    const preset = $c424a9d7bd65f9e1$var$PRESETS[name];
    if (!preset) return;
    preset.forEach((p, i)=>{
        $c424a9d7bd65f9e1$var$steps[i].active = p.active;
        $c424a9d7bd65f9e1$var$steps[i].note = p.note;
        const btn = document.querySelector(`.seq-step[data-step="${i}"]`);
        if (btn) {
            btn.classList.toggle('seq-step--active', p.active);
            btn.setAttribute('aria-pressed', String(p.active));
        }
        const sel = document.querySelector(`.seq-note-select[data-step="${i}"]`);
        if (sel) sel.value = p.note;
    });
}
function $c424a9d7bd65f9e1$var$clearGrid() {
    for(let i = 0; i < $c424a9d7bd65f9e1$var$STEPS; i++){
        $c424a9d7bd65f9e1$var$steps[i].active = false;
        $c424a9d7bd65f9e1$var$steps[i].note = $c424a9d7bd65f9e1$var$DEFAULT_NOTE;
        const btn = document.querySelector(`.seq-step[data-step="${i}"]`);
        if (btn) {
            btn.classList.remove('seq-step--active');
            btn.setAttribute('aria-pressed', 'false');
        }
        const sel = document.querySelector(`.seq-note-select[data-step="${i}"]`);
        if (sel) sel.value = $c424a9d7bd65f9e1$var$DEFAULT_NOTE;
    }
}
// ─── Playhead ─────────────────────────────────────────────────────────────────
let $c424a9d7bd65f9e1$var$lastPlayheadStep = -1;
function $c424a9d7bd65f9e1$var$updatePlayhead(stepIdx) {
    if (!$c424a9d7bd65f9e1$var$isPlaying) return;
    // Remove previous highlight
    if ($c424a9d7bd65f9e1$var$lastPlayheadStep >= 0) {
        const prev = document.querySelector(`.seq-step[data-step="${$c424a9d7bd65f9e1$var$lastPlayheadStep}"]`);
        prev?.classList.remove('seq-step--playing');
    }
    const cur = document.querySelector(`.seq-step[data-step="${stepIdx}"]`);
    cur?.classList.add('seq-step--playing');
    $c424a9d7bd65f9e1$var$lastPlayheadStep = stepIdx;
}
function $c424a9d7bd65f9e1$var$clearPlayhead() {
    if ($c424a9d7bd65f9e1$var$lastPlayheadStep >= 0) {
        const prev = document.querySelector(`.seq-step[data-step="${$c424a9d7bd65f9e1$var$lastPlayheadStep}"]`);
        prev?.classList.remove('seq-step--playing');
        $c424a9d7bd65f9e1$var$lastPlayheadStep = -1;
    }
}
// ─── Play button ──────────────────────────────────────────────────────────────
function $c424a9d7bd65f9e1$var$updatePlayBtn() {
    const btn = document.getElementById('seq-play-btn');
    if (!btn) return;
    btn.setAttribute('aria-pressed', String($c424a9d7bd65f9e1$var$isPlaying));
    btn.textContent = $c424a9d7bd65f9e1$var$isPlaying ? "\u25A0 Stop" : "\u25B6 Play";
}
// ─── Controls ─────────────────────────────────────────────────────────────────
/** Format a time value (seconds) as a human-readable string. */ function $c424a9d7bd65f9e1$var$formatTime(s) {
    return s < 1 ? `${Math.round(s * 1000)}ms` : `${s.toFixed(2)}s`;
}
function $c424a9d7bd65f9e1$var$initControls() {
    // Play / stop
    document.getElementById('seq-play-btn')?.addEventListener('click', $c424a9d7bd65f9e1$var$togglePlay);
    // Presets
    document.querySelectorAll('.preset-btn').forEach((btn)=>{
        btn.addEventListener('click', ()=>{
            const name = btn.dataset.preset;
            if (name) $c424a9d7bd65f9e1$var$applyPreset(name);
        });
    });
    // Clear
    document.getElementById('seq-clear-btn')?.addEventListener('click', $c424a9d7bd65f9e1$var$clearGrid);
    // Note length
    const noteLenInput = document.getElementById('seq-note-len');
    const noteLenValue = document.getElementById('seq-note-len-value');
    noteLenInput?.addEventListener('input', ()=>{
        $c424a9d7bd65f9e1$var$params.noteLen = parseFloat(noteLenInput.value);
        if (noteLenValue) noteLenValue.textContent = `${Math.round($c424a9d7bd65f9e1$var$params.noteLen * 100)}%`;
    });
    if (noteLenValue && noteLenInput) noteLenValue.textContent = `${Math.round(parseFloat(noteLenInput.value) * 100)}%`;
    // Waveform
    const waveformSel = document.getElementById('seq-waveform');
    waveformSel?.addEventListener('change', ()=>{
        $c424a9d7bd65f9e1$var$params.waveform = waveformSel.value;
    });
    // Volume
    const volInput = document.getElementById('seq-volume');
    const volValue = document.getElementById('seq-volume-value');
    volInput?.addEventListener('input', ()=>{
        $c424a9d7bd65f9e1$var$params.volume = parseFloat(volInput.value);
        if (volValue) volValue.textContent = `${Math.round($c424a9d7bd65f9e1$var$params.volume * 100)}%`;
        if ($c424a9d7bd65f9e1$var$bassGain && $c424a9d7bd65f9e1$var$audioCtx) $c424a9d7bd65f9e1$var$bassGain.gain.setTargetAtTime($c424a9d7bd65f9e1$var$params.volume, $c424a9d7bd65f9e1$var$audioCtx.currentTime, 0.01);
    });
    if (volValue && volInput) volValue.textContent = `${Math.round(parseFloat(volInput.value) * 100)}%`;
    // ADSR sliders
    const adsrSliders = [
        {
            id: 'seq-attack',
            valueId: 'seq-attack-value',
            param: 'attack',
            format: $c424a9d7bd65f9e1$var$formatTime
        },
        {
            id: 'seq-decay',
            valueId: 'seq-decay-value',
            param: 'decay',
            format: $c424a9d7bd65f9e1$var$formatTime
        },
        {
            id: 'seq-sustain',
            valueId: 'seq-sustain-value',
            param: 'sustain',
            format: (v)=>`${Math.round(v * 100)}%`
        },
        {
            id: 'seq-release',
            valueId: 'seq-release-value',
            param: 'release',
            format: $c424a9d7bd65f9e1$var$formatTime
        }
    ];
    adsrSliders.forEach(({ id: id, valueId: valueId, param: param, format: format })=>{
        const input = document.getElementById(id);
        const display = document.getElementById(valueId);
        if (!input) return;
        input.addEventListener('input', ()=>{
            const v = parseFloat(input.value);
            $c424a9d7bd65f9e1$var$params[param] = v;
            if (display) display.textContent = format(v);
        });
        if (display) display.textContent = format(parseFloat(input.value));
    });
}
function $c424a9d7bd65f9e1$export$b1dc82c371d172b3(bpm) {
    $c424a9d7bd65f9e1$var$params.bpm = bpm;
}
function $c424a9d7bd65f9e1$export$202069deb4777b8d() {
    if ($c424a9d7bd65f9e1$var$isPlaying) $c424a9d7bd65f9e1$var$stopSequencer();
}
function $c424a9d7bd65f9e1$export$e6fcb677180ad105() {
    return $c424a9d7bd65f9e1$var$bassGain;
}
/**
 * initBassSequencer — called by instruments.js when the Bass Sequencer tab
 * panel is first activated (or on page load).  Safe to call multiple times;
 * subsequent calls are no-ops because the grid is only built once.
 */ let $c424a9d7bd65f9e1$var$_initialized = false;
function $c424a9d7bd65f9e1$export$dda539be2dfae10() {
    if ($c424a9d7bd65f9e1$var$_initialized) return;
    $c424a9d7bd65f9e1$var$_initialized = true;
    $c424a9d7bd65f9e1$var$buildGrid();
    $c424a9d7bd65f9e1$var$initControls();
}


/**
 * mixer.js — Multi-track audio level control for the Studio page
 *
 * Features:
 *  - Per-track volume fader (0–100%, with dB display)
 *  - Per-track pan knob (L–R, -100 to +100)
 *  - Mute and Solo buttons per track
 *  - Real-time VU meter per track (peak + RMS via AnalyserNode)
 *  - Master bus volume fader
 *  - Master VU meter
 *  - All nodes wired into the shared AudioContext from instruments.js
 *
 * Architecture
 * ────────────
 *  Each instrument module exposes a per-module GainNode (drumGain, bassGain,
 *  synthMasterGain) that already sits between the voices and the shared
 *  masterGain.  The mixer inserts a new layer of nodes *between* the per-
 *  module gain and the shared masterGain:
 *
 *    instrument voices
 *         │
 *    [module gain]   ← owned by drum-machine / bass-sequencer / instruments
 *         │
 *    [track fader]   ← GainNode, value = fader level (0–1)
 *         │
 *    [panner]        ← StereoPannerNode, value = pan (-1 … +1)
 *         │
 *    [mute gain]     ← GainNode, value = 0 (muted) or 1 (unmuted)
 *         │
 *    [analyser]      ← AnalyserNode, feeds VU meter
 *         │
 *    [sharedMasterGain]
 *         │
 *    [masterAnalyser] ← feeds master VU meter
 *         │
 *    audioCtx.destination (+ recordingDest)
 *
 * Because the module gains are already connected to sharedMasterGain by
 * instruments.js, we *disconnect* them from sharedMasterGain and re-connect
 * them through the mixer chain.  This is safe because disconnect() without
 * arguments removes all existing connections from that node.
 *
 * The mixer is initialised lazily when its tab is first activated, after
 * instruments.js has already set up the shared context.
 */ // ─── Track definitions ────────────────────────────────────────────────────────
/**
 * Each track descriptor binds a human-readable label to the GainNode that
 * the corresponding instrument module exposes.  The `getGain` function is
 * called at init time (after the shared context is ready) to retrieve the
 * live GainNode.
 *
 * @type {Array<{id: string, label: string, color: string, defaultVolume: number}>}
 */ const $a5ac5192ee7b1e67$var$TRACK_DEFS = [
    {
        id: 'synth',
        label: 'Synth',
        color: '#a8d8f5',
        defaultVolume: 0.80
    },
    {
        id: 'drums',
        label: 'Drum Machine',
        color: '#a8f5a8',
        defaultVolume: 0.80
    },
    {
        id: 'bass',
        label: 'Bass Sequencer',
        color: '#f5d0a8',
        defaultVolume: 0.80
    }
];
// ─── State ────────────────────────────────────────────────────────────────────
/** Shared AudioContext injected by instruments.js */ let $a5ac5192ee7b1e67$var$audioCtx = null;
/** The shared masterGain node from instruments.js */ let $a5ac5192ee7b1e67$var$sharedMasterGain = null;
/**
 * Per-track mixer nodes and state.
 * Populated by initMixerGraph().
 *
 * @type {Map<string, {
 *   trackFader: GainNode,
 *   panner: StereoPannerNode,
 *   muteGain: GainNode,
 *   analyser: AnalyserNode,
 *   volume: number,
 *   pan: number,
 *   muted: boolean,
 *   soloed: boolean,
 * }>}
 */ const $a5ac5192ee7b1e67$var$tracks = new Map();
/** Master analyser node (sits after sharedMasterGain). */ let $a5ac5192ee7b1e67$var$masterAnalyser = null;
/** Whether any track is currently soloed. */ let $a5ac5192ee7b1e67$var$anySoloed = false;
/** rAF handle for the VU meter animation loop. */ let $a5ac5192ee7b1e67$var$vuRafHandle = null;
function $a5ac5192ee7b1e67$export$b4bdf1ab72187a15(ctx, masterGain, moduleGains) {
    $a5ac5192ee7b1e67$var$audioCtx = ctx;
    $a5ac5192ee7b1e67$var$sharedMasterGain = masterGain;
    $a5ac5192ee7b1e67$var$buildMixerGraph(moduleGains);
    $a5ac5192ee7b1e67$var$buildMixerUI();
    $a5ac5192ee7b1e67$var$startVuLoop();
}
// ─── Audio graph ──────────────────────────────────────────────────────────────
/**
 * Insert the mixer node chain between each module gain and sharedMasterGain.
 *
 * @param {{ synth: GainNode, drums: GainNode, bass: GainNode }} moduleGains
 */ function $a5ac5192ee7b1e67$var$buildMixerGraph(moduleGains) {
    $a5ac5192ee7b1e67$var$TRACK_DEFS.forEach((def)=>{
        const moduleGain = moduleGains[def.id];
        if (!moduleGain) return;
        // Disconnect the module gain from wherever it was previously connected
        // (instruments.js connected it directly to sharedMasterGain).
        try {
            moduleGain.disconnect();
        } catch  {}
        // ── Track fader (volume) ──
        const trackFader = $a5ac5192ee7b1e67$var$audioCtx.createGain();
        trackFader.gain.value = def.defaultVolume;
        // ── Stereo panner ──
        let panner;
        if (typeof $a5ac5192ee7b1e67$var$audioCtx.createStereoPanner === 'function') {
            panner = $a5ac5192ee7b1e67$var$audioCtx.createStereoPanner();
            panner.pan.value = 0;
        } else {
            // Fallback: plain gain node (no panning) for very old browsers
            panner = $a5ac5192ee7b1e67$var$audioCtx.createGain();
            panner.gain.value = 1;
            panner._isFallback = true;
        }
        // ── Mute gain ──
        const muteGain = $a5ac5192ee7b1e67$var$audioCtx.createGain();
        muteGain.gain.value = 1; // unmuted
        // ── Analyser (feeds VU meter) ──
        const analyser = $a5ac5192ee7b1e67$var$audioCtx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.6;
        // Wire: moduleGain → trackFader → panner → muteGain → analyser → masterGain
        moduleGain.connect(trackFader);
        trackFader.connect(panner);
        panner.connect(muteGain);
        muteGain.connect(analyser);
        analyser.connect($a5ac5192ee7b1e67$var$sharedMasterGain);
        $a5ac5192ee7b1e67$var$tracks.set(def.id, {
            moduleGain: moduleGain,
            trackFader: trackFader,
            panner: panner,
            muteGain: muteGain,
            analyser: analyser,
            volume: def.defaultVolume,
            pan: 0,
            muted: false,
            soloed: false
        });
    });
    // Master analyser — sits after sharedMasterGain, before destination
    $a5ac5192ee7b1e67$var$masterAnalyser = $a5ac5192ee7b1e67$var$audioCtx.createAnalyser();
    $a5ac5192ee7b1e67$var$masterAnalyser.fftSize = 256;
    $a5ac5192ee7b1e67$var$masterAnalyser.smoothingTimeConstant = 0.6;
    // sharedMasterGain already connects to destination; we tap in parallel
    $a5ac5192ee7b1e67$var$sharedMasterGain.connect($a5ac5192ee7b1e67$var$masterAnalyser);
    // masterAnalyser output goes to destination as well so we can hear it
    // (connecting to destination twice is fine — Web Audio sums the signals,
    //  but since we're only tapping for analysis we connect to a silent gain)
    const silentGain = $a5ac5192ee7b1e67$var$audioCtx.createGain();
    silentGain.gain.value = 0;
    $a5ac5192ee7b1e67$var$masterAnalyser.connect(silentGain);
    silentGain.connect($a5ac5192ee7b1e67$var$audioCtx.destination);
}
// ─── UI builder ───────────────────────────────────────────────────────────────
function $a5ac5192ee7b1e67$var$buildMixerUI() {
    const container = document.getElementById('mixer-tracks');
    if (!container) return;
    container.innerHTML = '';
    $a5ac5192ee7b1e67$var$TRACK_DEFS.forEach((def)=>{
        const track = $a5ac5192ee7b1e67$var$tracks.get(def.id);
        if (!track) return;
        const card = document.createElement('div');
        card.className = 'mixer-track';
        card.dataset.trackId = def.id;
        // Colour accent strip at the top
        card.style.setProperty('--track-color', def.color);
        // Interpolates only TRACK_DEFS, a hardcoded module constant.
        // No user or server input reaches this template.
        // eslint-disable-next-line no-restricted-syntax
        card.innerHTML = `
      <div class="mixer-track-header">
        <span class="mixer-track-name">${def.label}</span>
        <div class="mixer-track-btns">
          <button class="mixer-btn mixer-mute-btn" data-track="${def.id}"
            aria-pressed="false" aria-label="Mute ${def.label}" type="button">M</button>
          <button class="mixer-btn mixer-solo-btn" data-track="${def.id}"
            aria-pressed="false" aria-label="Solo ${def.label}" type="button">S</button>
        </div>
      </div>

      <div class="mixer-vu-wrap" aria-hidden="true">
        <canvas class="mixer-vu-canvas" id="vu-${def.id}" width="28" height="120"></canvas>
      </div>

      <div class="mixer-fader-wrap">
        <label class="mixer-label" for="fader-${def.id}">Vol</label>
        <input type="range" class="mixer-fader" id="fader-${def.id}"
          data-track="${def.id}"
          min="0" max="1" step="0.01"
          value="${def.defaultVolume}"
          aria-label="${def.label} volume"
          aria-valuetext="${Math.round(def.defaultVolume * 100)}%">
        <span class="mixer-fader-value" id="fader-val-${def.id}">${Math.round(def.defaultVolume * 100)}%</span>
      </div>

      <div class="mixer-pan-wrap">
        <label class="mixer-label" for="pan-${def.id}">Pan</label>
        <input type="range" class="mixer-pan" id="pan-${def.id}"
          data-track="${def.id}"
          min="-1" max="1" step="0.01"
          value="0"
          aria-label="${def.label} pan"
          aria-valuetext="Center">
        <span class="mixer-pan-value" id="pan-val-${def.id}">C</span>
      </div>
    `;
        container.appendChild(card);
    });
    // Master channel
    const masterCard = document.createElement('div');
    masterCard.className = 'mixer-track mixer-master';
    masterCard.innerHTML = `
    <div class="mixer-track-header">
      <span class="mixer-track-name">Master</span>
    </div>

    <div class="mixer-vu-wrap" aria-hidden="true">
      <canvas class="mixer-vu-canvas" id="vu-master" width="28" height="120"></canvas>
    </div>

    <div class="mixer-fader-wrap">
      <label class="mixer-label" for="fader-master">Vol</label>
      <input type="range" class="mixer-fader" id="fader-master"
        min="0" max="1" step="0.01"
        value="0.8"
        aria-label="Master volume"
        aria-valuetext="80%">
      <span class="mixer-fader-value" id="fader-val-master">80%</span>
    </div>
  `;
    container.appendChild(masterCard);
    $a5ac5192ee7b1e67$var$attachMixerEvents();
}
function $a5ac5192ee7b1e67$var$attachMixerEvents() {
    // Volume faders
    document.querySelectorAll('.mixer-fader[data-track]').forEach((input)=>{
        input.addEventListener('input', ()=>{
            const trackId = input.dataset.track;
            const v = parseFloat(input.value);
            $a5ac5192ee7b1e67$var$setTrackVolume(trackId, v);
        });
    });
    // Master fader (no data-track)
    const masterFader = document.getElementById('fader-master');
    const masterVal = document.getElementById('fader-val-master');
    masterFader?.addEventListener('input', ()=>{
        const v = parseFloat(masterFader.value);
        if (masterVal) masterVal.textContent = `${Math.round(v * 100)}%`;
        if ($a5ac5192ee7b1e67$var$sharedMasterGain && $a5ac5192ee7b1e67$var$audioCtx) $a5ac5192ee7b1e67$var$sharedMasterGain.gain.setTargetAtTime(v, $a5ac5192ee7b1e67$var$audioCtx.currentTime, 0.01);
        masterFader.setAttribute('aria-valuetext', `${Math.round(v * 100)}%`);
    });
    // Pan knobs
    document.querySelectorAll('.mixer-pan[data-track]').forEach((input)=>{
        input.addEventListener('input', ()=>{
            const trackId = input.dataset.track;
            const v = parseFloat(input.value);
            $a5ac5192ee7b1e67$var$setTrackPan(trackId, v);
        });
    });
    // Mute buttons
    document.querySelectorAll('.mixer-mute-btn').forEach((btn)=>{
        btn.addEventListener('click', ()=>{
            const trackId = btn.dataset.track;
            $a5ac5192ee7b1e67$var$toggleMute(trackId);
        });
    });
    // Solo buttons
    document.querySelectorAll('.mixer-solo-btn').forEach((btn)=>{
        btn.addEventListener('click', ()=>{
            const trackId = btn.dataset.track;
            $a5ac5192ee7b1e67$var$toggleSolo(trackId);
        });
    });
}
// ─── Track controls ───────────────────────────────────────────────────────────
function $a5ac5192ee7b1e67$var$setTrackVolume(trackId, value) {
    const track = $a5ac5192ee7b1e67$var$tracks.get(trackId);
    if (!track) return;
    track.volume = value;
    if ($a5ac5192ee7b1e67$var$audioCtx && track.trackFader) track.trackFader.gain.setTargetAtTime(value, $a5ac5192ee7b1e67$var$audioCtx.currentTime, 0.01);
    const display = document.getElementById(`fader-val-${trackId}`);
    if (display) display.textContent = `${Math.round(value * 100)}%`;
    const input = document.getElementById(`fader-${trackId}`);
    if (input) input.setAttribute('aria-valuetext', `${Math.round(value * 100)}%`);
}
function $a5ac5192ee7b1e67$var$setTrackPan(trackId, value) {
    const track = $a5ac5192ee7b1e67$var$tracks.get(trackId);
    if (!track || !track.panner) return;
    track.pan = value;
    if (!track.panner._isFallback && $a5ac5192ee7b1e67$var$audioCtx) track.panner.pan.setTargetAtTime(value, $a5ac5192ee7b1e67$var$audioCtx.currentTime, 0.01);
    const display = document.getElementById(`pan-val-${trackId}`);
    if (display) {
        if (Math.abs(value) < 0.02) display.textContent = 'C';
        else {
            const side = value < 0 ? 'L' : 'R';
            display.textContent = `${side}${Math.round(Math.abs(value) * 100)}`;
        }
    }
    const input = document.getElementById(`pan-${trackId}`);
    if (input) {
        const label = Math.abs(value) < 0.02 ? 'Center' : value < 0 ? `Left ${Math.round(Math.abs(value) * 100)}` : `Right ${Math.round(value * 100)}`;
        input.setAttribute('aria-valuetext', label);
    }
}
function $a5ac5192ee7b1e67$var$toggleMute(trackId) {
    const track = $a5ac5192ee7b1e67$var$tracks.get(trackId);
    if (!track) return;
    track.muted = !track.muted;
    $a5ac5192ee7b1e67$var$applyMuteState(trackId, track);
    const btn = document.querySelector(`.mixer-mute-btn[data-track="${trackId}"]`);
    if (btn) {
        btn.setAttribute('aria-pressed', String(track.muted));
        btn.classList.toggle('mixer-btn--active', track.muted);
    }
}
function $a5ac5192ee7b1e67$var$toggleSolo(trackId) {
    const track = $a5ac5192ee7b1e67$var$tracks.get(trackId);
    if (!track) return;
    track.soloed = !track.soloed;
    // Update the global solo flag
    $a5ac5192ee7b1e67$var$anySoloed = [
        ...$a5ac5192ee7b1e67$var$tracks.values()
    ].some((t)=>t.soloed);
    // Re-evaluate mute state for every track
    $a5ac5192ee7b1e67$var$tracks.forEach((t, id)=>$a5ac5192ee7b1e67$var$applyMuteState(id, t));
    const btn = document.querySelector(`.mixer-solo-btn[data-track="${trackId}"]`);
    if (btn) {
        btn.setAttribute('aria-pressed', String(track.soloed));
        btn.classList.toggle('mixer-btn--active', track.soloed);
    }
}
/**
 * Apply the effective mute state for a track, taking solo into account.
 * A track is silent when:
 *   - it is individually muted, OR
 *   - some other track is soloed and this track is not
 */ function $a5ac5192ee7b1e67$var$applyMuteState(trackId, track) {
    const effectivelySilent = track.muted || $a5ac5192ee7b1e67$var$anySoloed && !track.soloed;
    const targetGain = effectivelySilent ? 0 : 1;
    if ($a5ac5192ee7b1e67$var$audioCtx && track.muteGain) track.muteGain.gain.setTargetAtTime(targetGain, $a5ac5192ee7b1e67$var$audioCtx.currentTime, 0.02);
    // Grey-out the track card when silent
    const card = document.querySelector(`.mixer-track[data-track-id="${trackId}"]`);
    card?.classList.toggle('mixer-track--silent', effectivelySilent);
}
// ─── VU meters ────────────────────────────────────────────────────────────────
/**
 * Read the time-domain data from an AnalyserNode and compute:
 *  - peak: max absolute sample value (0–1)
 *  - rms:  root-mean-square of the buffer (0–1)
 */ function $a5ac5192ee7b1e67$var$getLevel(analyser) {
    if (!analyser) return {
        peak: 0,
        rms: 0
    };
    const buf = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(buf);
    let sumSq = 0;
    let peak = 0;
    for(let i = 0; i < buf.length; i++){
        const abs = Math.abs(buf[i]);
        if (abs > peak) peak = abs;
        sumSq += abs * abs;
    }
    return {
        peak: peak,
        rms: Math.sqrt(sumSq / buf.length)
    };
}
/**
 * Draw a simple two-channel VU bar on a canvas element.
 *
 * The bar is split into three colour zones:
 *  - Green  (0 – 70%): safe
 *  - Yellow (70 – 90%): moderate
 *  - Red    (90 – 100%): clipping risk
 *
 * @param {HTMLCanvasElement} canvas
 * @param {number} level  0–1 normalised level
 * @param {string} trackColor  CSS colour for the track accent (used for green zone)
 */ function $a5ac5192ee7b1e67$var$drawVU(canvas, level, trackColor = '#a8f5a8') {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const filled = Math.round(level * h);
    // Background
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(0, 0, w, h);
    if (filled <= 0) return;
    // Determine colour zones
    const greenH = Math.round(h * 0.70);
    const yellowH = Math.round(h * 0.90);
    // Draw from the bottom up
    const barY = h - filled;
    if (barY < greenH) {
        // Red zone (top)
        ctx.fillStyle = '#f5a8a8';
        ctx.fillRect(0, barY, w, Math.min(filled, h - yellowH));
    }
    if (barY < yellowH) {
        // Yellow zone
        const yStart = Math.max(barY, h - yellowH);
        const yEnd = h - greenH;
        if (yEnd > yStart) {
            ctx.fillStyle = '#f5e6a8';
            ctx.fillRect(0, yStart, w, yEnd - yStart);
        }
    }
    // Green zone (bottom)
    const gStart = Math.max(barY, h - greenH);
    if (gStart < h) {
        ctx.fillStyle = trackColor;
        ctx.fillRect(0, gStart, w, h - gStart);
    }
    // Segment lines (tick marks every ~10px)
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    for(let y = 0; y < h; y += 10)ctx.fillRect(0, y, w, 1);
}
/** Peak hold values per track for the VU meter (decay slowly). */ const $a5ac5192ee7b1e67$var$peakHold = new Map();
function $a5ac5192ee7b1e67$var$startVuLoop() {
    if ($a5ac5192ee7b1e67$var$vuRafHandle) return; // already running
    function loop() {
        $a5ac5192ee7b1e67$var$TRACK_DEFS.forEach((def)=>{
            const track = $a5ac5192ee7b1e67$var$tracks.get(def.id);
            const canvas = document.getElementById(`vu-${def.id}`);
            if (!track || !canvas) return;
            const { rms: rms } = $a5ac5192ee7b1e67$var$getLevel(track.analyser);
            // Peak hold with slow decay
            const prev = $a5ac5192ee7b1e67$var$peakHold.get(def.id) || 0;
            const held = Math.max(rms, prev * 0.97); // decay by 3% per frame
            $a5ac5192ee7b1e67$var$peakHold.set(def.id, held);
            $a5ac5192ee7b1e67$var$drawVU(canvas, held, def.color);
        });
        // Master VU
        const masterCanvas = document.getElementById('vu-master');
        if (masterCanvas && $a5ac5192ee7b1e67$var$masterAnalyser) {
            const { rms: rms } = $a5ac5192ee7b1e67$var$getLevel($a5ac5192ee7b1e67$var$masterAnalyser);
            const prev = $a5ac5192ee7b1e67$var$peakHold.get('master') || 0;
            const held = Math.max(rms, prev * 0.97);
            $a5ac5192ee7b1e67$var$peakHold.set('master', held);
            $a5ac5192ee7b1e67$var$drawVU(masterCanvas, held, '#e0c8f5');
        }
        $a5ac5192ee7b1e67$var$vuRafHandle = requestAnimationFrame(loop);
    }
    $a5ac5192ee7b1e67$var$vuRafHandle = requestAnimationFrame(loop);
}
function $a5ac5192ee7b1e67$export$7ae285eefd413ec() {
    if ($a5ac5192ee7b1e67$var$vuRafHandle) {
        cancelAnimationFrame($a5ac5192ee7b1e67$var$vuRafHandle);
        $a5ac5192ee7b1e67$var$vuRafHandle = null;
    }
}


const $d746eebf4eb62eb7$var$NOTE_FREQ_OCT4 = {
    C: 261.63,
    'C#': 277.18,
    D: 293.66,
    'D#': 311.13,
    E: 329.63,
    F: 349.23,
    'F#': 369.99,
    G: 391.99,
    'G#': 415.30,
    A: 440.00,
    'A#': 466.16,
    B: 493.88
};
/** Chromatic scale order used for rendering the keyboard. */ const $d746eebf4eb62eb7$var$CHROMATIC = [
    'C',
    'C#',
    'D',
    'D#',
    'E',
    'F',
    'F#',
    'G',
    'G#',
    'A',
    'A#',
    'B'
];
/** Notes that are "black keys" (sharps). */ const $d746eebf4eb62eb7$var$BLACK_KEYS = new Set([
    'C#',
    'D#',
    'F#',
    'G#',
    'A#'
]);
// ─── Computer-keyboard → note mapping ────────────────────────────────────────
//
// Layout mirrors a piano keyboard across two rows:
//
//   S   D       G   H   J
//  C# D#      F#  G#  A#
// Z  X  C  V  B  N  M
// C  D  E  F  G  A  B
//
const $d746eebf4eb62eb7$var$KB_MAP = {
    // White keys (bottom row)
    z: 'C',
    x: 'D',
    c: 'E',
    v: 'F',
    b: 'G',
    n: 'A',
    m: 'B',
    // Black keys (top row, interleaved)
    s: 'C#',
    d: 'D#',
    g: 'F#',
    h: 'G#',
    j: 'A#'
};
// ─── State ────────────────────────────────────────────────────────────────────
/** Web Audio context — created lazily on first user gesture. */ let $d746eebf4eb62eb7$var$audioCtx = null;
/** Master gain node (volume). Created with audioCtx. */ let $d746eebf4eb62eb7$var$masterGain = null;
/**
 * Dedicated gain node for the synth voices.
 * Sits between envGain nodes and masterGain so the mixer can intercept it.
 */ let $d746eebf4eb62eb7$var$synthGain = null;
/**
 * Map of currently-sounding notes.
 * Key: string like "C#-4" (note + octave).
 * Value: { osc: OscillatorNode, gain: GainNode }
 */ const $d746eebf4eb62eb7$var$activeNotes = new Map();
/** Keys currently pressed via computer keyboard (prevents key-repeat). */ const $d746eebf4eb62eb7$var$heldKeys = new Set();
/** Current octave (4 = middle C octave). */ let $d746eebf4eb62eb7$var$currentOctave = 4;
/** Synth parameters — kept in sync with the UI controls. */ const $d746eebf4eb62eb7$var$params = {
    waveform: 'sine',
    attack: 0.01,
    decay: 0.10,
    sustain: 0.70,
    release: 0.50,
    volume: 0.30
};
// ─── Audio context ────────────────────────────────────────────────────────────
/**
 * Create (if needed) and resume the AudioContext.
 *
 * iOS Safari rule: the AudioContext must be *created* **and** *resumed* inside
 * a synchronous trusted user-gesture handler (tap / click).  Calling resume()
 * from a promise callback or a setTimeout is not sufficient.
 *
 * This function is therefore registered directly on `click` and `touchstart`
 * at the document level so it fires as early as possible — before any note
 * logic runs.  The `{ once: true }` flag is intentionally NOT used here;
 * instead we guard with an `if (audioCtx.state === 'running') return` check so
 * that the listener is removed as soon as the context is confirmed running,
 * while still re-attempting on subsequent taps if the first gesture somehow
 * left the context suspended (e.g. background tab on iOS).
 */ function $d746eebf4eb62eb7$var$initAudioContext() {
    // `webkitAudioContext` fallback covers older Safari (pre-14.1)
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    // Create the context on the very first gesture
    if (!$d746eebf4eb62eb7$var$audioCtx) {
        $d746eebf4eb62eb7$var$audioCtx = new AudioContextClass();
        $d746eebf4eb62eb7$var$masterGain = $d746eebf4eb62eb7$var$audioCtx.createGain();
        $d746eebf4eb62eb7$var$masterGain.gain.value = $d746eebf4eb62eb7$var$params.volume;
        $d746eebf4eb62eb7$var$masterGain.connect($d746eebf4eb62eb7$var$audioCtx.destination);
        // Synth-specific gain node — intercept point for the mixer
        $d746eebf4eb62eb7$var$synthGain = $d746eebf4eb62eb7$var$audioCtx.createGain();
        $d746eebf4eb62eb7$var$synthGain.gain.value = 1.0;
        $d746eebf4eb62eb7$var$synthGain.connect($d746eebf4eb62eb7$var$masterGain);
    }
    // Resume if suspended — must happen synchronously inside the gesture handler
    if ($d746eebf4eb62eb7$var$audioCtx.state === 'suspended') $d746eebf4eb62eb7$var$audioCtx.resume().then(()=>{
        console.log('[instruments] AudioContext resumed, state:', $d746eebf4eb62eb7$var$audioCtx.state);
        // Refresh the MIDI banner now that audio is confirmed running
        // (the banner may have shown "no MIDI device" before audio was ready)
        $d746eebf4eb62eb7$var$refreshMidiStatus();
    }).catch((err)=>{
        console.warn('[instruments] AudioContext resume failed:', err);
    });
    // Once the context is running we no longer need to intercept every gesture
    if ($d746eebf4eb62eb7$var$audioCtx.state === 'running') {
        document.removeEventListener('click', $d746eebf4eb62eb7$var$initAudioContext);
        document.removeEventListener('touchstart', $d746eebf4eb62eb7$var$initAudioContext);
    }
}
// Register on both event types so the context is unlocked by any first
// interaction — whether the user taps a key, clicks a control, or just
// taps anywhere on the page.
document.addEventListener('click', $d746eebf4eb62eb7$var$initAudioContext, {
    passive: true
});
document.addEventListener('touchstart', $d746eebf4eb62eb7$var$initAudioContext, {
    passive: true
});
/**
 * Secondary safety net called from `noteOn` and other audio-triggering paths.
 * By the time a note fires the context should already be running (unlocked by
 * `initAudioContext` above), but we defend against edge cases such as:
 *  - MIDI note arriving before any touch (unlikely but possible on desktop)
 *  - Context suspended again after a background/foreground cycle
 */ function $d746eebf4eb62eb7$var$ensureAudioContext() {
    if (!$d746eebf4eb62eb7$var$audioCtx) {
        // Context hasn't been created yet — create it now (desktop / non-iOS path)
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;
        $d746eebf4eb62eb7$var$audioCtx = new AudioContextClass();
        $d746eebf4eb62eb7$var$masterGain = $d746eebf4eb62eb7$var$audioCtx.createGain();
        $d746eebf4eb62eb7$var$masterGain.gain.value = $d746eebf4eb62eb7$var$params.volume;
        $d746eebf4eb62eb7$var$masterGain.connect($d746eebf4eb62eb7$var$audioCtx.destination);
        // Synth-specific gain node — intercept point for the mixer
        $d746eebf4eb62eb7$var$synthGain = $d746eebf4eb62eb7$var$audioCtx.createGain();
        $d746eebf4eb62eb7$var$synthGain.gain.value = 1.0;
        $d746eebf4eb62eb7$var$synthGain.connect($d746eebf4eb62eb7$var$masterGain);
    }
    // Resume if suspended (autoplay policy — common on mobile Safari)
    if ($d746eebf4eb62eb7$var$audioCtx.state === 'suspended') $d746eebf4eb62eb7$var$audioCtx.resume().catch((err)=>{
        console.warn('[instruments] ensureAudioContext resume failed:', err);
    });
}
/**
 * Re-evaluate and update the MIDI status banner.
 * Called after the AudioContext is confirmed running so the UI reflects
 * the current MIDI connection state without an extra user action.
 */ function $d746eebf4eb62eb7$var$refreshMidiStatus() {
    if (!navigator.requestMIDIAccess) $d746eebf4eb62eb7$var$setMidiStatus("MIDI not supported in this browser \u2014 keyboard & mouse still work", 'unsupported');
// If MIDI was already initialised (midiAccess stored) the onstatechange
// handler will keep the banner current; nothing extra needed here.
}
// ─── Synth engine ─────────────────────────────────────────────────────────────
/**
 * Return the frequency (Hz) for a note name at a given octave.
 * Uses equal temperament: freq = baseFreq × 2^(octave - 4)
 */ function $d746eebf4eb62eb7$var$getFrequency(noteName, octave) {
    return $d746eebf4eb62eb7$var$NOTE_FREQ_OCT4[noteName] * Math.pow(2, octave - 4);
}
/**
 * Start a note (note-on).
 *
 * Safari fix (2a): We always anchor the gain timeline with
 * `setValueAtTime(0, now)` before the first `linearRampToValueAtTime`.
 * Without this anchor, Safari's scheduler can produce a click or skip
 * the ramp entirely.
 *
 * @param {string} noteName  e.g. "C#"
 * @param {number} octave
 * @param {number} velocity  0–1 (MIDI velocity mapped to 0–1)
 */ function $d746eebf4eb62eb7$var$noteOn(noteName, octave, velocity = 1.0) {
    $d746eebf4eb62eb7$var$ensureAudioContext();
    if (!$d746eebf4eb62eb7$var$audioCtx) return;
    const key = `${noteName}-${octave}`;
    if ($d746eebf4eb62eb7$var$activeNotes.has(key)) return; // already playing
    const freq = $d746eebf4eb62eb7$var$getFrequency(noteName, octave);
    const now = $d746eebf4eb62eb7$var$audioCtx.currentTime;
    // Oscillator
    const osc = $d746eebf4eb62eb7$var$audioCtx.createOscillator();
    osc.type = $d746eebf4eb62eb7$var$params.waveform;
    osc.frequency.value = freq;
    // Per-note gain (envelope)
    const envGain = $d746eebf4eb62eb7$var$audioCtx.createGain();
    // Safari fix (2a): anchor at 0 before ramping up
    envGain.gain.setValueAtTime(0, now);
    // Attack — ramp from 0 to velocity over attack time
    // Use a small epsilon so the ramp has a non-zero duration, which
    // avoids a Safari edge case where a zero-duration ramp is ignored.
    const attackEnd = now + Math.max($d746eebf4eb62eb7$var$params.attack, 0.001);
    envGain.gain.linearRampToValueAtTime(velocity, attackEnd);
    // Decay → Sustain
    const decayEnd = attackEnd + Math.max($d746eebf4eb62eb7$var$params.decay, 0.001);
    envGain.gain.linearRampToValueAtTime($d746eebf4eb62eb7$var$params.sustain * velocity, decayEnd);
    osc.connect(envGain);
    envGain.connect($d746eebf4eb62eb7$var$synthGain ?? $d746eebf4eb62eb7$var$masterGain);
    osc.start(now);
    $d746eebf4eb62eb7$var$activeNotes.set(key, {
        osc: osc,
        gain: envGain
    });
    // Highlight on-screen key
    $d746eebf4eb62eb7$var$setKeyActive(noteName, true);
}
/**
 * Release a note (note-off).
 *
 * Safari fix (2b): After `cancelScheduledValues` we immediately call
 * `setValueAtTime` at the same timestamp so Safari's scheduler has a
 * concrete starting point for the release ramp.
 *
 * Safari fix (2c): Guard `osc.stop()` so it never receives a time in
 * the past, which throws a DOMException in Safari.
 *
 * @param {string} noteName
 * @param {number} octave
 */ function $d746eebf4eb62eb7$var$noteOff(noteName, octave) {
    const key = `${noteName}-${octave}`;
    const node = $d746eebf4eb62eb7$var$activeNotes.get(key);
    if (!node) return;
    const { osc: osc, gain: envGain } = node;
    const now = $d746eebf4eb62eb7$var$audioCtx.currentTime;
    // Safari fix (2b): cancel then immediately re-anchor the current value
    envGain.gain.cancelScheduledValues(now);
    envGain.gain.setValueAtTime(envGain.gain.value, now);
    const releaseEnd = now + Math.max($d746eebf4eb62eb7$var$params.release, 0.001);
    envGain.gain.linearRampToValueAtTime(0, releaseEnd);
    // Safari fix (2c): never pass a stop time that is already in the past
    const stopTime = Math.max(releaseEnd + 0.01, $d746eebf4eb62eb7$var$audioCtx.currentTime + 0.001);
    try {
        osc.stop(stopTime);
    } catch  {
        // Fallback: stop immediately if the scheduled time is invalid
        try {
            osc.stop();
        } catch  {}
    }
    $d746eebf4eb62eb7$var$activeNotes.delete(key);
    // Remove highlight if no other octave is playing the same note name
    const stillActive = [
        ...$d746eebf4eb62eb7$var$activeNotes.keys()
    ].some((k)=>k.startsWith(`${noteName}-`));
    if (!stillActive) $d746eebf4eb62eb7$var$setKeyActive(noteName, false);
}
/** Release all currently-sounding notes immediately (e.g. on blur). */ function $d746eebf4eb62eb7$var$allNotesOff() {
    for (const [, { osc: osc, gain: envGain }] of $d746eebf4eb62eb7$var$activeNotes){
        if (!$d746eebf4eb62eb7$var$audioCtx) break;
        const now = $d746eebf4eb62eb7$var$audioCtx.currentTime;
        envGain.gain.cancelScheduledValues(now);
        envGain.gain.setValueAtTime(0, now);
        try {
            osc.stop(Math.max(now + 0.01, $d746eebf4eb62eb7$var$audioCtx.currentTime + 0.001));
        } catch  {}
    }
    $d746eebf4eb62eb7$var$activeNotes.clear();
    $d746eebf4eb62eb7$var$heldKeys.clear();
    // Clear all visual highlights
    document.querySelectorAll('.key.active').forEach((el)=>el.classList.remove('active'));
}
// ─── On-screen keyboard ───────────────────────────────────────────────────────
function $d746eebf4eb62eb7$var$buildKeyboard() {
    const container = document.getElementById('keyboard');
    if (!container) return;
    container.innerHTML = '';
    $d746eebf4eb62eb7$var$CHROMATIC.forEach((note)=>{
        const btn = document.createElement('button');
        btn.className = `key${$d746eebf4eb62eb7$var$BLACK_KEYS.has(note) ? ' black-key' : ''}`;
        btn.dataset.note = note;
        btn.textContent = note;
        btn.setAttribute('aria-label', `Play ${note}`);
        btn.setAttribute('type', 'button');
        // Track whether this button's current press was initiated via a pointer
        // event so that the touch fallback doesn't double-trigger on browsers
        // that fire both (e.g. Chrome on Android, some iPad browsers).
        let pressedViaPointer = false;
        // ── Pointer events (mouse + stylus + modern touch) ────────────────────
        btn.addEventListener('pointerdown', (e)=>{
            e.preventDefault(); // prevent focus stealing / scroll
            pressedViaPointer = true;
            $d746eebf4eb62eb7$var$noteOn(note, $d746eebf4eb62eb7$var$currentOctave, 0.8);
        });
        btn.addEventListener('pointerup', ()=>{
            pressedViaPointer = false;
            $d746eebf4eb62eb7$var$noteOff(note, $d746eebf4eb62eb7$var$currentOctave);
        });
        btn.addEventListener('pointerleave', ()=>{
            pressedViaPointer = false;
            $d746eebf4eb62eb7$var$noteOff(note, $d746eebf4eb62eb7$var$currentOctave);
        });
        // ── Touch events — Safari iOS fallback ────────────────────────────────
        // iOS Safari fires pointer events but can miss pointerup/pointerleave
        // when a touch ends outside the element. touchend / touchcancel are
        // always reliable on Safari. We skip these if pointerdown already fired
        // to avoid double-triggering on browsers that support both event models.
        btn.addEventListener('touchstart', (e)=>{
            if (pressedViaPointer) return; // already handled by pointerdown
            e.preventDefault();
            $d746eebf4eb62eb7$var$noteOn(note, $d746eebf4eb62eb7$var$currentOctave, 0.8);
        }, {
            passive: false
        });
        btn.addEventListener('touchend', (e)=>{
            if (pressedViaPointer) return;
            e.preventDefault();
            $d746eebf4eb62eb7$var$noteOff(note, $d746eebf4eb62eb7$var$currentOctave);
        }, {
            passive: false
        });
        btn.addEventListener('touchcancel', (e)=>{
            if (pressedViaPointer) return;
            e.preventDefault();
            $d746eebf4eb62eb7$var$noteOff(note, $d746eebf4eb62eb7$var$currentOctave);
        }, {
            passive: false
        });
        container.appendChild(btn);
    });
}
function $d746eebf4eb62eb7$var$setKeyActive(noteName, active) {
    const btn = document.querySelector(`.key[data-note="${noteName}"]`);
    if (!btn) return;
    btn.classList.toggle('active', active);
}
// ─── Computer keyboard input ──────────────────────────────────────────────────
function $d746eebf4eb62eb7$var$handleKeyDown(e) {
    // Ignore when focus is inside a form control
    if ([
        'INPUT',
        'SELECT',
        'TEXTAREA'
    ].includes(e.target.tagName)) return;
    if (e.repeat) return;
    // Octave shift
    if (e.key === 'ArrowUp') {
        e.preventDefault();
        $d746eebf4eb62eb7$var$changeOctave(1);
        return;
    }
    if (e.key === 'ArrowDown') {
        e.preventDefault();
        $d746eebf4eb62eb7$var$changeOctave(-1);
        return;
    }
    // R — toggle recording
    if (e.key.toLowerCase() === 'r') {
        e.preventDefault();
        if ($d746eebf4eb62eb7$var$mediaRecorder) $d746eebf4eb62eb7$var$stopRecording();
        else $d746eebf4eb62eb7$var$startRecording();
        return;
    }
    const note = $d746eebf4eb62eb7$var$KB_MAP[e.key.toLowerCase()];
    if (!note || $d746eebf4eb62eb7$var$heldKeys.has(e.key.toLowerCase())) return;
    $d746eebf4eb62eb7$var$heldKeys.add(e.key.toLowerCase());
    $d746eebf4eb62eb7$var$noteOn(note, $d746eebf4eb62eb7$var$currentOctave, 0.8);
}
function $d746eebf4eb62eb7$var$handleKeyUp(e) {
    if ([
        'INPUT',
        'SELECT',
        'TEXTAREA'
    ].includes(e.target.tagName)) return;
    const note = $d746eebf4eb62eb7$var$KB_MAP[e.key.toLowerCase()];
    if (!note) return;
    $d746eebf4eb62eb7$var$heldKeys.delete(e.key.toLowerCase());
    $d746eebf4eb62eb7$var$noteOff(note, $d746eebf4eb62eb7$var$currentOctave);
}
// ─── Octave control ───────────────────────────────────────────────────────────
function $d746eebf4eb62eb7$var$changeOctave(delta) {
    const next = $d746eebf4eb62eb7$var$currentOctave + delta;
    if (next < 0 || next > 8) return;
    // Release any held notes before shifting so they don't hang
    $d746eebf4eb62eb7$var$allNotesOff();
    $d746eebf4eb62eb7$var$currentOctave = next;
    const display = document.getElementById('octave-value');
    if (display) display.textContent = $d746eebf4eb62eb7$var$currentOctave;
}
// ─── MIDI ─────────────────────────────────────────────────────────────────────
/** MIDI note number → { noteName, octave } */ function $d746eebf4eb62eb7$var$midiNoteToNameOctave(midiNote) {
    const octave = Math.floor(midiNote / 12) - 1;
    const noteName = $d746eebf4eb62eb7$var$CHROMATIC[midiNote % 12];
    return {
        noteName: noteName,
        octave: octave
    };
}
function $d746eebf4eb62eb7$var$onMidiMessage(event) {
    const [status, note, velocity] = event.data;
    const command = status & 0xf0;
    // Flash MIDI indicator
    $d746eebf4eb62eb7$var$flashMidiIndicator();
    if (command === 0x90 && velocity > 0) {
        // Note On
        const { noteName: noteName, octave: octave } = $d746eebf4eb62eb7$var$midiNoteToNameOctave(note);
        $d746eebf4eb62eb7$var$noteOn(noteName, octave, velocity / 127);
    } else if (command === 0x80 || command === 0x90 && velocity === 0) {
        // Note Off (0x80) or Note On with velocity 0 (running status)
        const { noteName: noteName, octave: octave } = $d746eebf4eb62eb7$var$midiNoteToNameOctave(note);
        $d746eebf4eb62eb7$var$noteOff(noteName, octave);
    }
// Other MIDI messages (CC, pitch bend, etc.) are ignored for now
}
function $d746eebf4eb62eb7$var$flashMidiIndicator() {
    const indicator = document.getElementById('midi-indicator');
    if (!indicator) return;
    indicator.classList.remove('active');
    // Force reflow so the animation restarts cleanly
    indicator.offsetWidth;
    indicator.classList.add('active');
}
function $d746eebf4eb62eb7$var$setMidiStatus(text, state = '') {
    const banner = document.getElementById('midi-status');
    const label = document.getElementById('midi-text');
    if (!banner || !label) return;
    banner.className = `midi-status${state ? ` ${state}` : ''}`;
    label.textContent = text;
}
async function $d746eebf4eb62eb7$var$initMidi() {
    // Web MIDI is not supported in Safari (as of 2025). Show a friendly
    // informational message rather than an alarming "error" state so that
    // Safari users understand the rest of the synth still works.
    if (!navigator.requestMIDIAccess) {
        $d746eebf4eb62eb7$var$setMidiStatus("MIDI not supported in this browser \u2014 keyboard & mouse still work", 'unsupported');
        return;
    }
    try {
        const midiAccess = await navigator.requestMIDIAccess({
            sysex: false
        });
        function connectInputs() {
            let count = 0;
            midiAccess.inputs.forEach((input)=>{
                input.onmidimessage = $d746eebf4eb62eb7$var$onMidiMessage;
                count++;
            });
            if (count > 0) {
                const names = [];
                midiAccess.inputs.forEach((i)=>names.push(i.name));
                $d746eebf4eb62eb7$var$setMidiStatus(`Connected: ${names.join(', ')}`, 'connected');
            } else $d746eebf4eb62eb7$var$setMidiStatus('No MIDI device connected');
        }
        connectInputs();
        // Re-scan when devices are added / removed
        midiAccess.onstatechange = ()=>connectInputs();
    } catch (err) {
        // User denied permission or MIDI access failed
        $d746eebf4eb62eb7$var$setMidiStatus(`MIDI unavailable: ${err.message}`, 'error');
    }
}
// ─── UI controls ──────────────────────────────────────────────────────────────
/** Format a seconds value as a human-readable time string. */ function $d746eebf4eb62eb7$var$formatTime(seconds) {
    if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
    return `${seconds.toFixed(2)}s`;
}
function $d746eebf4eb62eb7$var$initControls() {
    // Waveform select
    const waveformEl = document.getElementById('waveform');
    waveformEl?.addEventListener('change', ()=>{
        $d746eebf4eb62eb7$var$params.waveform = waveformEl.value;
        // Update any currently-playing oscillators
        $d746eebf4eb62eb7$var$activeNotes.forEach(({ osc: osc })=>{
            osc.type = $d746eebf4eb62eb7$var$params.waveform;
        });
    });
    // ADSR + Volume sliders
    const sliders = [
        {
            id: 'attack',
            valueId: 'attack-value',
            param: 'attack',
            format: (v)=>$d746eebf4eb62eb7$var$formatTime(v)
        },
        {
            id: 'decay',
            valueId: 'decay-value',
            param: 'decay',
            format: (v)=>$d746eebf4eb62eb7$var$formatTime(v)
        },
        {
            id: 'sustain',
            valueId: 'sustain-value',
            param: 'sustain',
            format: (v)=>`${Math.round(v * 100)}%`
        },
        {
            id: 'release',
            valueId: 'release-value',
            param: 'release',
            format: (v)=>$d746eebf4eb62eb7$var$formatTime(v)
        },
        {
            id: 'volume',
            valueId: 'volume-value',
            param: 'volume',
            format: (v)=>`${Math.round(v * 100)}%`
        }
    ];
    sliders.forEach(({ id: id, valueId: valueId, param: param, format: format })=>{
        const input = document.getElementById(id);
        const display = document.getElementById(valueId);
        if (!input) return;
        // Sync display on input (live feedback while dragging)
        input.addEventListener('input', ()=>{
            const v = parseFloat(input.value);
            $d746eebf4eb62eb7$var$params[param] = v;
            if (display) display.textContent = format(v);
            // Apply volume change immediately to master gain
            if (param === 'volume' && $d746eebf4eb62eb7$var$masterGain && $d746eebf4eb62eb7$var$audioCtx) {
                $d746eebf4eb62eb7$var$masterGain.gain.setTargetAtTime(v, $d746eebf4eb62eb7$var$audioCtx.currentTime, 0.01);
                // Also keep synthGain in step with the volume slider
                if ($d746eebf4eb62eb7$var$synthGain) $d746eebf4eb62eb7$var$synthGain.gain.setTargetAtTime(v, $d746eebf4eb62eb7$var$audioCtx.currentTime, 0.01);
            }
        });
        // Initialise display text from the default slider value
        if (display) display.textContent = format(parseFloat(input.value));
    });
    // Octave buttons
    document.getElementById('octave-down')?.addEventListener('click', ()=>$d746eebf4eb62eb7$var$changeOctave(-1));
    document.getElementById('octave-up')?.addEventListener('click', ()=>$d746eebf4eb62eb7$var$changeOctave(1));
}
// ─── Cleanup ──────────────────────────────────────────────────────────────────
/** Release notes when the window loses focus so keys don't "stick". */ window.addEventListener('blur', ()=>{
    if ($d746eebf4eb62eb7$var$activeNotes.size > 0) $d746eebf4eb62eb7$var$allNotesOff();
});
// ─── Tab navigation ───────────────────────────────────────────────────────────
/**
 * Simple accessible tab switcher.
 *
 * - Tabs use role="tab" / aria-selected / aria-controls.
 * - Panels use role="tabpanel" / aria-labelledby.
 * - Arrow-key navigation between tabs (ARIA authoring practice).
 * - Shows the universal transport bar when a sequencer tab is active.
 */ function $d746eebf4eb62eb7$var$initTabs() {
    const tabList = document.querySelector('[role="tablist"]');
    if (!tabList) return;
    const tabs = [
        ...tabList.querySelectorAll('[role="tab"]')
    ];
    const panels = tabs.map((t)=>document.getElementById(t.getAttribute('aria-controls')));
    function activateTab(tab) {
        tabs.forEach((t, i)=>{
            const isSelected = t === tab;
            t.setAttribute('aria-selected', String(isSelected));
            if (panels[i]) panels[i].classList.toggle('tab-panel--active', isSelected);
        });
        // Lazily initialise the bass sequencer the first time its tab is shown
        if (tab.id === 'tab-bass') (0, $c424a9d7bd65f9e1$export$dda539be2dfae10)();
    }
    tabs.forEach((tab, i)=>{
        tab.addEventListener('click', ()=>activateTab(tab));
        // Arrow-key navigation
        tab.addEventListener('keydown', (e)=>{
            let next = null;
            if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
            else if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
            else if (e.key === 'Home') next = tabs[0];
            else if (e.key === 'End') next = tabs[tabs.length - 1];
            if (next) {
                e.preventDefault();
                next.focus();
                activateTab(next);
            }
        });
    });
}
// ─── Universal transport (BPM + Stop All) ────────────────────────────────────
function $d746eebf4eb62eb7$var$initUniversalTransport() {
    const bpmInput = document.getElementById('universal-bpm');
    const bpmValue = document.getElementById('universal-bpm-value');
    const stopAllBtn = document.getElementById('stop-all-btn');
    if (bpmInput) {
        bpmInput.addEventListener('input', ()=>{
            const bpm = parseInt(bpmInput.value, 10);
            if (bpmValue) bpmValue.textContent = bpm;
            // Push to both sequencers
            (0, $ce32644a0c65456a$export$f8f2055a7d4756f6)(bpm);
            (0, $c424a9d7bd65f9e1$export$b1dc82c371d172b3)(bpm);
        });
        if (bpmValue) bpmValue.textContent = bpmInput.value;
    }
    if (stopAllBtn) stopAllBtn.addEventListener('click', ()=>{
        (0, $ce32644a0c65456a$export$ec09ef61b8a66e8f)();
        (0, $c424a9d7bd65f9e1$export$202069deb4777b8d)();
    });
}
// ─── Recording System ─────────────────────────────────────────────────────────
//
// All three instruments (synth, drum machine, bass sequencer) share the single
// AudioContext owned by instruments.js. The drum and bass modules receive this
// context via setSharedAudioContext() at boot time, so every node in every
// module belongs to the same context graph.
//
// This means we only need ONE MediaStreamDestination — we connect the shared
// masterGain (which already receives all instrument output through the mixer
// gain nodes exported by each module) to that destination and record it.
//
// Architecture:
//   synthEnvGain  ──┐
//   drumMasterGain──┤──► sharedMasterGain ──► audioCtx.destination
//   bassMasterGain──┘         │
//                             └──► recordingDest (when recording)
//
// ─────────────────────────────────────────────────────────────────────────────
let $d746eebf4eb62eb7$var$mediaRecorder = null; // used as a boolean flag: non-null = currently recording
let $d746eebf4eb62eb7$var$recordingStartTime = 0;
let $d746eebf4eb62eb7$var$recordingTimerInterval = null;
/** Sentinel set once the ScriptProcessorNode capture graph is wired up. */ let $d746eebf4eb62eb7$var$recordingDest = null;
/**
 * In-memory list of completed recordings.
 * Each entry: { blob: Blob, name: string, duration: number }
 */ const $d746eebf4eb62eb7$var$recordings = [];
// ─── WAV encoder ─────────────────────────────────────────────────────────────
//
// We capture raw PCM samples via a ScriptProcessorNode rather than using
// MediaRecorder. This gives us a plain Float32Array of audio data that we
// encode ourselves as a standard WAV file.
//
// Why WAV instead of WebM/MP4?
//   • WebM is not supported by Apple QuickTime or iOS at all.
//   • MP4/AAC is only produced by MediaRecorder in Safari — Chrome/Firefox
//     produce WebM even when you ask for audio/mp4.
//   • WAV (PCM) is universally supported: macOS, iOS, Windows, Android,
//     every DAW, every media player — with no codec dependencies.
//   • The files are larger (≈10 MB/min at 44.1 kHz stereo) but perfectly
//     fine for short instrument recordings.
//
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Write a standard 16-bit PCM WAV file from an array of Float32 sample buffers.
 *
 * @param {Float32Array[]} leftChannels   - Collected left-channel buffers
 * @param {Float32Array[]} rightChannels  - Collected right-channel buffers (may equal left for mono)
 * @param {number}         sampleRate
 * @returns {Blob}  audio/wav Blob
 */ function $d746eebf4eb62eb7$var$encodeWAV(leftChannels, rightChannels, sampleRate) {
    // Flatten the per-buffer arrays into one contiguous Float32Array each
    const totalSamples = leftChannels.reduce((n, b)=>n + b.length, 0);
    const left = new Float32Array(totalSamples);
    const right = new Float32Array(totalSamples);
    let offset = 0;
    for(let i = 0; i < leftChannels.length; i++){
        left.set(leftChannels[i], offset);
        right.set(rightChannels[i], offset);
        offset += leftChannels[i].length;
    }
    const numChannels = 2;
    const bitsPerSample = 16;
    const byteRate = sampleRate * numChannels * (bitsPerSample / 8);
    const blockAlign = numChannels * (bitsPerSample / 8);
    const dataBytes = totalSamples * numChannels * (bitsPerSample / 8);
    const buffer = new ArrayBuffer(44 + dataBytes);
    const view = new DataView(buffer);
    // ── RIFF header ──
    $d746eebf4eb62eb7$var$writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataBytes, true);
    $d746eebf4eb62eb7$var$writeString(view, 8, 'WAVE');
    // ── fmt  chunk ──
    $d746eebf4eb62eb7$var$writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // chunk size
    view.setUint16(20, 1, true); // PCM = 1
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    // ── data chunk ──
    $d746eebf4eb62eb7$var$writeString(view, 36, 'data');
    view.setUint32(40, dataBytes, true);
    // Interleave L/R samples and clamp to int16
    let sampleOffset = 44;
    for(let i = 0; i < totalSamples; i++){
        view.setInt16(sampleOffset, $d746eebf4eb62eb7$var$clampInt16(left[i]), true);
        sampleOffset += 2;
        view.setInt16(sampleOffset, $d746eebf4eb62eb7$var$clampInt16(right[i]), true);
        sampleOffset += 2;
    }
    return new Blob([
        buffer
    ], {
        type: 'audio/wav'
    });
}
function $d746eebf4eb62eb7$var$writeString(view, offset, str) {
    for(let i = 0; i < str.length; i++)view.setUint8(offset + i, str.charCodeAt(i));
}
function $d746eebf4eb62eb7$var$clampInt16(sample) {
    const s = Math.max(-1, Math.min(1, sample));
    return s < 0 ? s * 0x8000 : s * 0x7FFF;
}
// ─── PCM capture via ScriptProcessorNode ─────────────────────────────────────
//
// ScriptProcessorNode is deprecated in favour of AudioWorklet but remains
// universally supported (including iOS Safari) and is simpler to set up
// without a separate worklet file. For short recordings the performance
// impact is negligible.
//
// Buffer size 4096 gives ~93 ms latency at 44.1 kHz — fine for recording.
/** @type {ScriptProcessorNode|null} */ let $d746eebf4eb62eb7$var$scriptProcessor = null;
/** Accumulated PCM sample buffers while recording. */ let $d746eebf4eb62eb7$var$pcmLeft = [];
let $d746eebf4eb62eb7$var$pcmRight = [];
/**
 * Ensure the shared AudioContext exists and the ScriptProcessorNode capture
 * graph is wired up. Safe to call multiple times — no-op after first call.
 *
 * Returns true if ready, false if the AudioContext hasn't been created yet.
 */ function $d746eebf4eb62eb7$var$ensureRecordingDest() {
    $d746eebf4eb62eb7$var$ensureAudioContext();
    if (!$d746eebf4eb62eb7$var$audioCtx || !$d746eebf4eb62eb7$var$masterGain) return false;
    if ($d746eebf4eb62eb7$var$recordingDest) return true; // already set up
    // ScriptProcessorNode: bufferSize, inputChannels, outputChannels
    $d746eebf4eb62eb7$var$scriptProcessor = $d746eebf4eb62eb7$var$audioCtx.createScriptProcessor(4096, 2, 2);
    $d746eebf4eb62eb7$var$scriptProcessor.onaudioprocess = (e)=>{
        if (!$d746eebf4eb62eb7$var$mediaRecorder) return; // only collect when recording flag is set
        // Copy (don't reference) the channel data — the buffer is reused
        $d746eebf4eb62eb7$var$pcmLeft.push(new Float32Array(e.inputBuffer.getChannelData(0)));
        // If the source is mono, mirror left into right
        const rightData = e.inputBuffer.numberOfChannels > 1 ? e.inputBuffer.getChannelData(1) : e.inputBuffer.getChannelData(0);
        $d746eebf4eb62eb7$var$pcmRight.push(new Float32Array(rightData));
    };
    // masterGain → scriptProcessor → (silent output, we only need the side-effect)
    $d746eebf4eb62eb7$var$masterGain.connect($d746eebf4eb62eb7$var$scriptProcessor);
    $d746eebf4eb62eb7$var$scriptProcessor.connect($d746eebf4eb62eb7$var$audioCtx.destination); // must connect to keep it running
    // We still create a MediaStreamDestination so we have a stream object
    // available (not strictly needed for WAV but keeps the architecture consistent)
    $d746eebf4eb62eb7$var$recordingDest = {
        stream: null
    }; // sentinel so the guard above works
    return true;
}
function $d746eebf4eb62eb7$var$startRecording() {
    if (!$d746eebf4eb62eb7$var$ensureRecordingDest()) {
        console.warn("[recording] AudioContext not ready \u2014 interact with the page first to unlock audio.");
        return;
    }
    // Use mediaRecorder as a simple "is recording" flag (non-null = recording)
    // We repurpose the variable name to avoid a larger refactor.
    $d746eebf4eb62eb7$var$mediaRecorder = true;
    $d746eebf4eb62eb7$var$pcmLeft = [];
    $d746eebf4eb62eb7$var$pcmRight = [];
    $d746eebf4eb62eb7$var$recordingStartTime = Date.now();
    // Update button UI
    const recordBtn = document.getElementById('record-btn');
    if (recordBtn) {
        recordBtn.setAttribute('aria-pressed', 'true');
        recordBtn.textContent = "\u23F9 Stop";
    }
    // Show and start the elapsed-time counter
    const recordTime = document.getElementById('record-time');
    if (recordTime) {
        recordTime.hidden = false;
        recordTime.textContent = '0:00';
    }
    $d746eebf4eb62eb7$var$recordingTimerInterval = setInterval(()=>{
        const elapsed = (Date.now() - $d746eebf4eb62eb7$var$recordingStartTime) / 1000;
        const mins = Math.floor(elapsed / 60);
        const secs = Math.floor(elapsed % 60);
        if (recordTime) recordTime.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
    }, 250);
}
function $d746eebf4eb62eb7$var$stopRecording() {
    if (!$d746eebf4eb62eb7$var$mediaRecorder) return; // not recording
    // Stop collecting samples
    $d746eebf4eb62eb7$var$mediaRecorder = null;
    clearInterval($d746eebf4eb62eb7$var$recordingTimerInterval);
    $d746eebf4eb62eb7$var$recordingTimerInterval = null;
    // Encode the collected PCM data as a WAV file
    const blob = $d746eebf4eb62eb7$var$encodeWAV($d746eebf4eb62eb7$var$pcmLeft, $d746eebf4eb62eb7$var$pcmRight, $d746eebf4eb62eb7$var$audioCtx.sampleRate);
    const duration = (Date.now() - $d746eebf4eb62eb7$var$recordingStartTime) / 1000;
    const name = `Recording ${new Date().toLocaleTimeString()}`;
    $d746eebf4eb62eb7$var$recordings.push({
        blob: blob,
        name: name,
        duration: duration
    });
    $d746eebf4eb62eb7$var$pcmLeft = [];
    $d746eebf4eb62eb7$var$pcmRight = [];
    $d746eebf4eb62eb7$var$renderRecordings();
    // Reset button UI
    const recordBtn = document.getElementById('record-btn');
    if (recordBtn) {
        recordBtn.setAttribute('aria-pressed', 'false');
        recordBtn.textContent = "\u25CF Record";
    }
    const recordTime = document.getElementById('record-time');
    if (recordTime) {
        recordTime.hidden = true;
        recordTime.textContent = '0:00';
    }
}
/**
 * Re-render the recordings list section.
 * Called after every add or delete.
 */ function $d746eebf4eb62eb7$var$renderRecordings() {
    const section = document.getElementById('recordings-section');
    const list = document.getElementById('recordings-list');
    if (!section || !list) return;
    if ($d746eebf4eb62eb7$var$recordings.length === 0) {
        section.hidden = true;
        return;
    }
    section.hidden = false;
    list.innerHTML = '';
    $d746eebf4eb62eb7$var$recordings.forEach((recording, index)=>{
        const card = document.createElement('div');
        card.className = 'recording-card';
        const mins = Math.floor(recording.duration / 60);
        const secs = Math.floor(recording.duration % 60);
        const durationStr = `${mins}:${secs.toString().padStart(2, '0')}`;
        // Create a fresh object URL for each render pass
        const url = URL.createObjectURL(recording.blob);
        const sizeKB = (recording.blob.size / 1024).toFixed(1);
        // Every value here is produced locally: a generated recording name,
        // formatted numbers, and a blob: URL from this page's own
        // MediaRecorder. Nothing round-trips a server.
        // eslint-disable-next-line no-restricted-syntax
        card.innerHTML = `
      <div class="recording-info">
        <div class="recording-name">${recording.name}</div>
        <div class="recording-meta">${durationStr} \u{2022} ${sizeKB} KB</div>
      </div>
      <audio controls src="${url}" preload="metadata"></audio>
      <div class="recording-controls">
        <button class="recording-btn" data-index="${index}" data-action="download"
          aria-label="Download ${recording.name}">
          \u{2193} Download
        </button>
        <button class="recording-btn recording-btn--delete" data-index="${index}" data-action="delete"
          aria-label="Delete ${recording.name}">
          \u{2715} Delete
        </button>
      </div>
    `;
        list.appendChild(card);
    });
    // Attach event listeners after innerHTML is set
    list.querySelectorAll('[data-action="download"]').forEach((btn)=>{
        btn.addEventListener('click', ()=>$d746eebf4eb62eb7$var$downloadRecording(parseInt(btn.dataset.index, 10)));
    });
    list.querySelectorAll('[data-action="delete"]').forEach((btn)=>{
        btn.addEventListener('click', ()=>$d746eebf4eb62eb7$var$deleteRecording(parseInt(btn.dataset.index, 10)));
    });
}
function $d746eebf4eb62eb7$var$downloadRecording(index) {
    const recording = $d746eebf4eb62eb7$var$recordings[index];
    if (!recording) return;
    const url = URL.createObjectURL(recording.blob);
    const filename = `${recording.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.wav`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke after a short delay to allow the download to start
    setTimeout(()=>URL.revokeObjectURL(url), 1000);
}
function $d746eebf4eb62eb7$var$deleteRecording(index) {
    $d746eebf4eb62eb7$var$recordings.splice(index, 1);
    $d746eebf4eb62eb7$var$renderRecordings();
}
/** Wire up the record button toggle. */ function $d746eebf4eb62eb7$var$attachRecordButton() {
    const recordBtn = document.getElementById('record-btn');
    if (!recordBtn) return;
    recordBtn.addEventListener('click', ()=>{
        if ($d746eebf4eb62eb7$var$mediaRecorder) $d746eebf4eb62eb7$var$stopRecording();
        else $d746eebf4eb62eb7$var$startRecording();
    });
}
function $d746eebf4eb62eb7$export$993a7abdece33906() {
    return $d746eebf4eb62eb7$var$synthGain;
}
$d746eebf4eb62eb7$var$buildKeyboard();
$d746eebf4eb62eb7$var$initControls();
$d746eebf4eb62eb7$var$initMidi();
$d746eebf4eb62eb7$var$initTabs();
$d746eebf4eb62eb7$var$initUniversalTransport();
// Ensure the synth AudioContext is created before injecting it into the
// sub-modules so all instruments share one context and one masterGain.
// initAudioContext() is registered on click/touchstart, so we call
// ensureAudioContext() here to create the context on desktop (no gesture
// needed) and then inject it. On iOS the context is created on first gesture
// via initAudioContext; we inject it at that point via the existing listeners.
$d746eebf4eb62eb7$var$ensureAudioContext();
if ($d746eebf4eb62eb7$var$audioCtx && $d746eebf4eb62eb7$var$masterGain) {
    (0, $ce32644a0c65456a$export$5dc7305e084869ea)($d746eebf4eb62eb7$var$audioCtx, $d746eebf4eb62eb7$var$masterGain);
    (0, $c424a9d7bd65f9e1$export$5dc7305e084869ea)($d746eebf4eb62eb7$var$audioCtx, $d746eebf4eb62eb7$var$masterGain);
}
// Also inject after the first user gesture resolves the AudioContext on iOS
document.addEventListener('click', ()=>{
    if ($d746eebf4eb62eb7$var$audioCtx && $d746eebf4eb62eb7$var$masterGain) {
        (0, $ce32644a0c65456a$export$5dc7305e084869ea)($d746eebf4eb62eb7$var$audioCtx, $d746eebf4eb62eb7$var$masterGain);
        (0, $c424a9d7bd65f9e1$export$5dc7305e084869ea)($d746eebf4eb62eb7$var$audioCtx, $d746eebf4eb62eb7$var$masterGain);
    }
}, {
    once: true,
    passive: true
});
(0, $ce32644a0c65456a$export$95dc3c842d06fdc6)();
$d746eebf4eb62eb7$var$attachRecordButton();
document.addEventListener('keydown', $d746eebf4eb62eb7$var$handleKeyDown);
document.addEventListener('keyup', $d746eebf4eb62eb7$var$handleKeyUp);
// ─── Mixer initialisation ─────────────────────────────────────────────────────
//
// The mixer needs the per-module gain nodes from drum-machine and bass-sequencer.
// Those gains are only created after setSharedAudioContext() is called, which
// happens after the first user gesture on iOS. We initialise the mixer lazily
// when the Mixer tab is first activated, by which point audio is guaranteed to
// be running.
let $d746eebf4eb62eb7$var$_mixerInitialized = false;
function $d746eebf4eb62eb7$var$maybeInitMixer() {
    if ($d746eebf4eb62eb7$var$_mixerInitialized) return;
    if (!$d746eebf4eb62eb7$var$audioCtx || !$d746eebf4eb62eb7$var$masterGain) return;
    const drumGainNode = (0, $ce32644a0c65456a$export$bd10d0af299f079e)?.();
    const bassGainNode = (0, $c424a9d7bd65f9e1$export$e6fcb677180ad105)?.();
    if (!drumGainNode || !bassGainNode) return;
    $d746eebf4eb62eb7$var$_mixerInitialized = true;
    (0, $a5ac5192ee7b1e67$export$b4bdf1ab72187a15)($d746eebf4eb62eb7$var$audioCtx, $d746eebf4eb62eb7$var$masterGain, {
        synth: $d746eebf4eb62eb7$export$993a7abdece33906(),
        drums: drumGainNode,
        bass: bassGainNode
    });
}
// Initialise the mixer the first time its tab becomes active. A
// MutationObserver on the panel avoids re-implementing initTabs().
(()=>{
    // Re-implement tab activation so we can hook into it without duplicating
    // the full initTabs() logic. We add a MutationObserver on the mixer panel
    // instead — simpler and avoids tight coupling.
    const mixerPanel = document.getElementById('panel-mixer');
    if (mixerPanel) {
        const observer = new MutationObserver(()=>{
            if (mixerPanel.classList.contains('tab-panel--active')) $d746eebf4eb62eb7$var$maybeInitMixer();
        });
        observer.observe(mixerPanel, {
            attributes: true,
            attributeFilter: [
                'class'
            ]
        });
    }
})();


//# sourceMappingURL=studio.9398a5c8.js.map
